// ============================================================================
// SS3 GOVERNMENT — COMPLETE LEARNING MODULE (13 TOPICS)
// NERDC Scheme of Work — First Term to Third Term (no Third Term in scheme)
// ============================================================================

export const WAEC_GOVERNMENT_LEARN_SS3 = [
  {
    "topic": "Military Rule in Nigeria",
    "topicCode": "SS3-GOV-01",
    "module": "Nigerian Political History",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Military rule</span> is a system in which the armed forces seize power, usually by a coup d'état, and govern the country. Nigeria was under military rule for about 29 of its years since independence, from the first coup in January 1966 to the return to civil rule in May 1999.</div><h3 class=\"learn-subheading\">1. Reasons for Military Intervention</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Reason</th><th>Explanation</th></tr></thead><tbody><tr><td>Political corruption and misrule</td><td>Civilian leaders were accused of embezzlement, nepotism and abuse of office.</td></tr><tr><td>Political instability</td><td>Crises such as the 1962–64 Western Region disturbances and disputed elections.</td></tr><tr><td>Ethnic and regional rivalry</td><td>Fears of domination by one region or ethnic group.</td></tr><tr><td>Economic mismanagement</td><td>Inflation, unemployment and poor handling of the economy.</td></tr><tr><td>Personal ambition</td><td>Some officers wanted power and its benefits.</td></tr><tr><td>Weak democratic institutions</td><td>Rigged elections and lack of respect for the rule of law invited intervention.</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Structure of Government Under Military Rule</h3><ul style=\"margin-left:20px;\"><li>The constitution was suspended or modified, and government ruled by <strong>decrees</strong>.</li><li>A supreme body (Supreme Military Council, Armed Forces Ruling Council or Provisional Ruling Council) made policy.</li><li>The Federal Executive Council (FEC) handled day-to-day administration.</li><li>Military governors, later joined by civilian commissioners, ran the states.</li><li>The legislature was dissolved and political parties banned; the judiciary was limited in what it could challenge.</li></ul><h3 class=\"learn-subheading\">3. Performance of Military Administrations</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Achievements</th><th>Shortcomings</th></tr></thead><tbody><tr><td>Won the civil war and reunited Nigeria (1967–70); creation of states; the 3Rs programme</td><td>Human rights abuses, detention without trial, press censorship</td></tr><tr><td>Development projects: roads, universities, oil-boom infrastructure, Abuja</td><td>Corruption and misuse of oil revenue</td></tr><tr><td>Handovers to civilians in 1979 and 1999</td><td>Annulment of the June 12, 1993 election; prolonged transitions</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Ways of Avoiding Military Coups</h3><ul style=\"margin-left:20px;\"><li>Practise good, transparent and accountable governance; fight corruption.</li><li>Hold free and fair elections and respect the rule of law.</li><li>Ensure equitable distribution of resources and fairness among ethnic groups.</li><li>Keep the military professional, well paid and under firm civilian control.</li><li>Educate citizens and the military on the value of democracy; strengthen democratic institutions.</li></ul><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Exam Tip:</strong> Remember the sequence of regimes: Ironsi (Jan 1966), Gowon (1966–75), Murtala/Obasanjo (1975–79), Buhari (1983–85), Babangida (1985–93), Abacha (1993–98), Abdulsalami (1998–99).</div>",
    "questions": [
      {
        "q": "Military rule is best defined as a system in which:",
        "o": [
          "citizens govern through referendums",
          "a president is elected by parliament",
          "a monarch rules by birthright",
          "the armed forces seize power and govern the country"
        ],
        "a": 3,
        "e": "Military rule occurs when the armed forces take over government, usually through a coup d'état.",
        "h": "Think of who holds power.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's first military coup took place in:",
        "o": [
          "December 1983",
          "October 1960",
          "July 1975",
          "January 1966"
        ],
        "a": 3,
        "e": "The first coup, on 15 January 1966, brought General Aguiyi-Ironsi to power.",
        "h": "It happened six years after independence.",
        "yr": "SS3"
      },
      {
        "q": "Who became Head of State after the first coup of 1966?",
        "o": [
          "General Yakubu Gowon",
          "General J.T.U. Aguiyi-Ironsi",
          "General Murtala Mohammed",
          "General Sani Abacha"
        ],
        "a": 1,
        "e": "Major-General Aguiyi-Ironsi took over after the January 1966 coup.",
        "h": "He was the most senior army officer.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a reason for military intervention in Nigeria?",
        "o": [
          "Peaceful and rigged-free elections",
          "Excellent economic management",
          "Corruption and political instability under civilian rule",
          "Strong democratic institutions"
        ],
        "a": 2,
        "e": "Corruption, instability and misrule were common reasons given for coups.",
        "h": "Consider what the coup makers accused civilians of.",
        "yr": "SS3"
      },
      {
        "q": "Under military rule, laws were made through:",
        "o": [
          "decrees",
          "referendums",
          "gubernatorial orders only",
          "bills passed by the Senate"
        ],
        "a": 0,
        "e": "The legislature was dissolved and the military issued decrees.",
        "h": "The word begins with 'd'.",
        "yr": "SS3"
      },
      {
        "q": "The body that made policy under General Ironsi and General Gowon was the:",
        "o": [
          "Senate",
          "National Assembly",
          "Council of State",
          "Supreme Military Council"
        ],
        "a": 3,
        "e": "The Supreme Military Council was the highest policy-making body.",
        "h": "It was made up of military officers.",
        "yr": "SS3"
      },
      {
        "q": "Who ruled Nigeria during the civil war of 1967–1970?",
        "o": [
          "General Muhammadu Buhari",
          "General Ibrahim Babangida",
          "General Yakubu Gowon",
          "General Murtala Mohammed"
        ],
        "a": 2,
        "e": "Gowon was Head of State from 1966 to 1975 and led Nigeria through the civil war.",
        "h": "He announced the '3Rs' after the war.",
        "yr": "SS3"
      },
      {
        "q": "Who handed over power to a civilian government in October 1979?",
        "o": [
          "General Abdulsalami Abubakar",
          "General Olusegun Obasanjo",
          "General Murtala Mohammed",
          "General Sani Abacha"
        ],
        "a": 1,
        "e": "Obasanjo handed over to Alhaji Shehu Shagari in 1979.",
        "h": "He later returned as a civilian president in 1999.",
        "yr": "SS3"
      },
      {
        "q": "The military coup of December 1983 overthrew the government of:",
        "o": [
          "Alhaji Shehu Shagari",
          "General Buhari",
          "Chief Ernest Shonekan",
          "General Gowon"
        ],
        "a": 0,
        "e": "General Buhari removed President Shagari's Second Republic government.",
        "h": "Shagari was Nigeria's first executive president.",
        "yr": "SS3"
      },
      {
        "q": "Which election annulment worsened the crisis of military rule in 1993?",
        "o": [
          "The 1999 election",
          "The 1979 election",
          "The June 12, 1993 presidential election",
          "The 1983 election"
        ],
        "a": 2,
        "e": "General Babangida annulled the widely believed victory of M.K.O. Abiola.",
        "h": "Its date is now marked as Democracy Day.",
        "yr": "SS3"
      },
      {
        "q": "Who was the last military Head of State before the 1999 handover?",
        "o": [
          "General Ibrahim Babangida",
          "General Abdulsalami Abubakar",
          "General Sani Abacha",
          "General Murtala Mohammed"
        ],
        "a": 1,
        "e": "Abdulsalami Abubakar took over after Abacha's death in 1998 and handed over in May 1999.",
        "h": "He came immediately after Abacha.",
        "yr": "SS3"
      },
      {
        "q": "A major achievement of military governments was:",
        "o": [
          "the creation of more states",
          "protection of press freedom",
          "the abolition of taxes",
          "the promotion of multi-party politics"
        ],
        "a": 0,
        "e": "Military regimes created states (12 in 1967, later 19, 21, 30 and 36).",
        "h": "It affected the map of Nigeria.",
        "yr": "SS3"
      },
      {
        "q": "A major shortcoming of military rule was:",
        "o": [
          "excessive respect for the constitution",
          "frequent free elections",
          "full independence of the legislature",
          "violation of human rights and press freedom"
        ],
        "a": 3,
        "e": "Detention without trial and censorship were common under military rule.",
        "h": "Consider civil liberties.",
        "yr": "SS3"
      },
      {
        "q": "Which of these helps to prevent military coups?",
        "o": [
          "Good governance and firm civilian control of the armed forces",
          "Ignoring the welfare of soldiers",
          "Election rigging",
          "Excluding minorities from government"
        ],
        "a": 0,
        "e": "Accountable, fair governance and a professional military discourage coups.",
        "h": "The best cure is good civilian rule.",
        "yr": "SS3"
      },
      {
        "q": "The 'Federal Executive Council' under military rule was mainly responsible for:",
        "o": [
          "passing bills into law",
          "electing the Head of State",
          "making court judgments",
          "day-to-day administration of the country"
        ],
        "a": 3,
        "e": "The FEC handled routine administration.",
        "h": "It carried out policy.",
        "yr": "SS3"
      },
      {
        "q": "The July 1975 coup that brought General Murtala Mohammed to power removed:",
        "o": [
          "General Aguiyi-Ironsi",
          "General Yakubu Gowon",
          "Alhaji Shehu Shagari",
          "General Sani Abacha"
        ],
        "a": 1,
        "e": "A bloodless coup in July 1975 removed Gowon while he was abroad.",
        "h": "Gowon had ruled since 1966.",
        "yr": "SS3"
      },
      {
        "q": "Which regime is associated with the transfer of the capital to Abuja decision (1976)?",
        "o": [
          "Abacha regime",
          "Murtala/Obasanjo regime",
          "Ironsi regime",
          "Buhari regime"
        ],
        "a": 1,
        "e": "The decision to relocate the capital from Lagos was made in 1976 under Murtala/Obasanjo.",
        "h": "It was taken in the mid-1970s.",
        "yr": "SS3"
      },
      {
        "q": "Military governors under military rule headed:",
        "o": [
          "the judiciary",
          "local councils only",
          "the Senate",
          "states"
        ],
        "a": 3,
        "e": "Each state was headed by a military governor or administrator.",
        "h": "They reported to the Head of State.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria returned to civil rule in:",
        "o": [
          "January 1966",
          "June 1993",
          "October 1979",
          "May 1999"
        ],
        "a": 3,
        "e": "The Fourth Republic began on 29 May 1999 with President Obasanjo.",
        "h": "It marks the beginning of the current democratic era.",
        "yr": "SS3"
      },
      {
        "q": "Which is a weakness of military rule in terms of the constitution?",
        "o": [
          "It introduced true judicial independence",
          "It increased legislative power",
          "The constitution was suspended or modified by decree",
          "It strengthened federalism"
        ],
        "a": 2,
        "e": "Military regimes ruled by decree over a suspended or modified constitution.",
        "h": "Think of law-making.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Conflict Resolution and Management",
    "topicCode": "SS3-GOV-02",
    "module": "Conflict and Peace",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">A <span class=\"learn-keyword\">conflict</span> is a disagreement or clash between individuals, groups or nations arising from differences in interests, values, beliefs or goals. Conflict is a normal part of social life, but it becomes harmful when handled badly.</div><h3 class=\"learn-subheading\">1. Causes of Conflict</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Cause</th><th>Example</th></tr></thead><tbody><tr><td>Competition over scarce resources</td><td>Land, oil, water, grazing routes</td></tr><tr><td>Ethnic and religious differences</td><td>Intolerance and marginalisation</td></tr><tr><td>Political power struggles</td><td>Election disputes, rigging, succession crises</td></tr><tr><td>Boundary and territorial disputes</td><td>Bakassi dispute between Nigeria and Cameroon</td></tr><tr><td>Injustice and inequality</td><td>Perceived unfair distribution of wealth and opportunity</td></tr><tr><td>Poor communication and misunderstanding</td><td>Rumour and mistrust</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Consequences of Conflict</h3><ul style=\"margin-left:20px;\"><li>Loss of lives and property; injuries and displacement of people.</li><li>Refugee problems and humanitarian crises.</li><li>Poverty, hunger and disruption of economic activities.</li><li>Breakdown of law and order; distrust between groups.</li><li>Poor image of the country and reduced foreign investment.</li></ul><h3 class=\"learn-subheading\">3. Ways of Resolving and Managing Conflict</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Method</th><th>Meaning</th></tr></thead><tbody><tr><td>Negotiation</td><td>Parties in dispute discuss directly to reach agreement.</td></tr><tr><td>Mediation</td><td>A neutral third party helps the parties reach a settlement but cannot impose it.</td></tr><tr><td>Conciliation</td><td>A third party works to reduce tension and bring the parties together.</td></tr><tr><td>Arbitration</td><td>A neutral arbiter hears both sides and gives a decision, often binding.</td></tr><tr><td>Adjudication</td><td>The dispute is settled by a court of law.</td></tr><tr><td>Diplomacy / dialogue</td><td>Peaceful talks, especially between nations.</td></tr></tbody></table></div><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">💡</span> <strong>Note:</strong> Mediation is advisory, arbitration is usually binding, and adjudication is by a court.</div>",
    "questions": [
      {
        "q": "A conflict is best described as:",
        "o": [
          "a peaceful agreement",
          "a disagreement or clash of interests between people or groups",
          "a type of election",
          "a treaty"
        ],
        "a": 1,
        "e": "Conflict arises from differences in interests, values or goals.",
        "h": "It is a clash.",
        "yr": "SS3"
      },
      {
        "q": "Which is a common cause of conflict?",
        "o": [
          "Equal sharing of resources",
          "Strong respect for law",
          "Perfect communication",
          "Competition for scarce resources"
        ],
        "a": 3,
        "e": "Scarce resources such as land and water often cause disputes.",
        "h": "Think of what people fight over.",
        "yr": "SS3"
      },
      {
        "q": "The Bakassi dispute was between Nigeria and:",
        "o": [
          "Cameroon",
          "Chad",
          "Benin",
          "Ghana"
        ],
        "a": 0,
        "e": "The dispute over the Bakassi Peninsula was settled by the ICJ in 2002.",
        "h": "It is Nigeria's eastern neighbour.",
        "yr": "SS3"
      },
      {
        "q": "Which court settled the Nigeria–Cameroon boundary dispute?",
        "o": [
          "ECOWAS Court",
          "Supreme Court of Nigeria",
          "Court of Appeal",
          "International Court of Justice"
        ],
        "a": 3,
        "e": "The ICJ at The Hague ruled in 2002.",
        "h": "It is the UN's judicial organ.",
        "yr": "SS3"
      },
      {
        "q": "One consequence of conflict is:",
        "o": [
          "economic growth",
          "increased foreign investment",
          "greater national unity",
          "loss of lives and property"
        ],
        "a": 3,
        "e": "Conflicts destroy lives, property and livelihoods.",
        "h": "It is harmful.",
        "yr": "SS3"
      },
      {
        "q": "Refugees and displaced persons are typically a result of:",
        "o": [
          "violent conflict",
          "good governance",
          "free trade",
          "peace education"
        ],
        "a": 0,
        "e": "Conflicts force people to flee their homes.",
        "h": "Think of people leaving their homes.",
        "yr": "SS3"
      },
      {
        "q": "In mediation, the third party:",
        "o": [
          "helps the parties reach agreement but cannot impose a decision",
          "acts as judge",
          "gives a binding judgment",
          "commands the parties"
        ],
        "a": 0,
        "e": "A mediator facilitates but has no power to impose settlement.",
        "h": "It is advisory.",
        "yr": "SS3"
      },
      {
        "q": "Which method usually produces a binding decision by a neutral person?",
        "o": [
          "Arbitration",
          "Mediation",
          "Dialogue",
          "Negotiation"
        ],
        "a": 0,
        "e": "An arbitrator hears both sides and gives a decision that is usually binding.",
        "h": "It resembles a private court.",
        "yr": "SS3"
      },
      {
        "q": "Settlement of a dispute by a court of law is called:",
        "o": [
          "adjudication",
          "conciliation",
          "mediation",
          "negotiation"
        ],
        "a": 0,
        "e": "Adjudication is the judicial settlement of a dispute.",
        "h": "It happens in a court.",
        "yr": "SS3"
      },
      {
        "q": "Direct discussion between the parties in a dispute is:",
        "o": [
          "sanction",
          "negotiation",
          "adjudication",
          "arbitration"
        ],
        "a": 1,
        "e": "In negotiation, the parties themselves discuss to reach agreement.",
        "h": "No third party is needed.",
        "yr": "SS3"
      },
      {
        "q": "Conciliation involves:",
        "o": [
          "a third party working to reduce tension and bring parties together",
          "war",
          "a court judgment",
          "an election"
        ],
        "a": 0,
        "e": "Conciliation aims at restoring dialogue and understanding.",
        "h": "It builds bridges.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a cause of political conflict?",
        "o": [
          "Power sharing agreed by all",
          "Respect for court rulings",
          "Disputed elections",
          "Fair elections accepted by all"
        ],
        "a": 2,
        "e": "Election disputes and power struggles often cause conflict.",
        "h": "Think of who wins power.",
        "yr": "SS3"
      },
      {
        "q": "Ethnic and religious intolerance leads to:",
        "o": [
          "national unity",
          "development",
          "conflict and violence",
          "peace"
        ],
        "a": 2,
        "e": "Intolerance and marginalisation are causes of conflict.",
        "h": "It divides people.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is NOT a peaceful way of resolving conflict?",
        "o": [
          "Use of force",
          "Arbitration",
          "Negotiation",
          "Mediation"
        ],
        "a": 0,
        "e": "Peaceful methods include dialogue, mediation and arbitration; force is not peaceful.",
        "h": "Pick the violent option.",
        "yr": "SS3"
      },
      {
        "q": "Conflict can affect the economy by:",
        "o": [
          "disrupting production and trade",
          "lowering prices of all goods",
          "increasing tourism",
          "raising investment"
        ],
        "a": 0,
        "e": "Conflict destroys economic activities and discourages investors.",
        "h": "Consider business.",
        "yr": "SS3"
      },
      {
        "q": "Poor communication may cause conflict because it leads to:",
        "o": [
          "clear agreements",
          "misunderstanding and mistrust",
          "peace",
          "tolerance"
        ],
        "a": 1,
        "e": "Rumour and misunderstanding can escalate disputes.",
        "h": "Think of misinformation.",
        "yr": "SS3"
      },
      {
        "q": "Injustice and inequality in the sharing of wealth can result in:",
        "o": [
          "grievances and agitation",
          "contentment",
          "efficient government",
          "industrial harmony"
        ],
        "a": 0,
        "e": "Perceived unfairness fuels agitation and conflict.",
        "h": "Feeling cheated.",
        "yr": "SS3"
      },
      {
        "q": "A neutral outsider helping two countries to settle a dispute is a:",
        "o": [
          "voter",
          "mediator",
          "prosecutor",
          "soldier"
        ],
        "a": 1,
        "e": "A mediator helps disputants reach agreement without imposing it.",
        "h": "The person is neutral.",
        "yr": "SS3"
      },
      {
        "q": "Which best describes conflict management?",
        "o": [
          "Encouraging conflict to grow",
          "Steps taken to control and reduce the harmful effects of conflict",
          "Ignoring disagreements",
          "Starting wars"
        ],
        "a": 1,
        "e": "Management aims to control, contain and resolve conflict.",
        "h": "It reduces harm.",
        "yr": "SS3"
      },
      {
        "q": "Which is the best way to prevent conflict in a diverse society?",
        "o": [
          "Suppressing complaints",
          "Justice, tolerance and fair treatment of all groups",
          "Ignoring minority rights",
          "Favouring one group"
        ],
        "a": 1,
        "e": "Fairness and tolerance reduce grievances that cause conflict.",
        "h": "Fair treatment.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Peace Education",
    "topicCode": "SS3-GOV-03",
    "module": "Conflict and Peace",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Peace</span> is more than the absence of war. It is a state of harmony, security, justice and mutual respect within and between societies. <span class=\"learn-keyword\">Peace education</span> teaches people the values, attitudes and skills needed to live peacefully.</div><h3 class=\"learn-subheading\">1. Meaning of Peace</h3><p class=\"learn-p\">Peace means a condition of order and calm in which individuals and groups live together without violence, fear or oppression, and where disagreements are settled by non-violent means.</p><h3 class=\"learn-subheading\">2. The Need for Peace</h3><ul style=\"margin-left:20px;\"><li>It protects lives and property.</li><li>It creates conditions for economic growth and development.</li><li>It allows democracy and good governance to thrive.</li><li>It encourages foreign investment and tourism.</li><li>It promotes unity, cooperation and national integration.</li><li>It allows citizens to enjoy fundamental human rights.</li></ul><h3 class=\"learn-subheading\">3. Ways of Promoting Peace</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Way</th><th>Explanation</th></tr></thead><tbody><tr><td>Justice and rule of law</td><td>Fair treatment for all citizens</td></tr><tr><td>Equitable distribution of resources</td><td>Reduces grievances and marginalisation</td></tr><tr><td>Tolerance and dialogue</td><td>Respect for religious, ethnic and political differences</td></tr><tr><td>Good governance</td><td>Transparency, accountability and inclusion</td></tr><tr><td>Peace education</td><td>Teaching non-violence in schools, homes and religious bodies</td></tr><tr><td>Regional and international cooperation</td><td>Peacekeeping by the UN, AU and ECOWAS</td></tr><tr><td>Free and fair elections</td><td>Prevents disputes over power</td></tr></tbody></table></div><div class=\"learn-tip-box\"><span class=\"learn-tip-icon\">🎯</span> <strong>Summary:</strong> Peace is built on justice, tolerance and good governance, not merely on the absence of fighting.</div>",
    "questions": [
      {
        "q": "Peace means:",
        "o": [
          "only the absence of war",
          "victory in battle",
          "a state of harmony, security and justice, not just the absence of war",
          "military power"
        ],
        "a": 2,
        "e": "Peace involves justice, order and mutual respect as well as the absence of violence.",
        "h": "It is more than no war.",
        "yr": "SS3"
      },
      {
        "q": "Peace education aims to:",
        "o": [
          "teach values and skills for peaceful living",
          "train soldiers",
          "promote conflict",
          "teach military strategy"
        ],
        "a": 0,
        "e": "Peace education teaches non-violence, tolerance and conflict-resolution skills.",
        "h": "It is a teaching programme.",
        "yr": "SS3"
      },
      {
        "q": "Why is peace necessary for national development?",
        "o": [
          "It creates conditions for economic growth and investment",
          "It stops trade",
          "It weakens democracy",
          "It discourages investors"
        ],
        "a": 0,
        "e": "Investors and businesses prefer stable, peaceful environments.",
        "h": "Think of business confidence.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following promotes peace?",
        "o": [
          "Justice and the rule of law",
          "Election rigging",
          "Corruption",
          "Discrimination"
        ],
        "a": 0,
        "e": "Fair treatment under the law reduces grievances.",
        "h": "Fairness.",
        "yr": "SS3"
      },
      {
        "q": "Peace helps citizens to enjoy:",
        "o": [
          "fundamental human rights",
          "curfews",
          "censorship",
          "forced labour"
        ],
        "a": 0,
        "e": "Where peace exists, rights are more easily protected.",
        "h": "Freedoms.",
        "yr": "SS3"
      },
      {
        "q": "Which organisation is involved in international peacekeeping?",
        "o": [
          "OPEC",
          "FIFA",
          "United Nations",
          "World Bank alone"
        ],
        "a": 2,
        "e": "The UN deploys peacekeepers to conflict areas.",
        "h": "It has blue-helmeted troops.",
        "yr": "SS3"
      },
      {
        "q": "Which regional bodies help to keep peace in Africa?",
        "o": [
          "UEFA and CAF",
          "OPEC and the IMF",
          "NATO and the EU",
          "AU and ECOWAS"
        ],
        "a": 3,
        "e": "The AU and ECOWAS undertake peacekeeping and mediation.",
        "h": "African organisations.",
        "yr": "SS3"
      },
      {
        "q": "Tolerance of religious and ethnic differences:",
        "o": [
          "encourages conflict",
          "weakens unity",
          "promotes peaceful coexistence",
          "causes war"
        ],
        "a": 2,
        "e": "Respecting differences reduces tension.",
        "h": "Living together.",
        "yr": "SS3"
      },
      {
        "q": "Equitable distribution of resources helps peace because it:",
        "o": [
          "increases inequality",
          "causes resentment",
          "reduces feelings of marginalisation",
          "encourages corruption"
        ],
        "a": 2,
        "e": "When resources are shared fairly, groups feel included.",
        "h": "Fair sharing.",
        "yr": "SS3"
      },
      {
        "q": "Free and fair elections promote peace by:",
        "o": [
          "causing violence",
          "excluding parties",
          "increasing rigging",
          "reducing disputes over who holds power"
        ],
        "a": 3,
        "e": "Credible elections give legitimacy to government.",
        "h": "Legitimacy.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a threat to peace?",
        "o": [
          "Marginalisation of groups",
          "Dialogue",
          "Good governance",
          "Tolerance"
        ],
        "a": 0,
        "e": "Feeling excluded can lead to violence.",
        "h": "Pick the negative.",
        "yr": "SS3"
      },
      {
        "q": "Peace education can be taught in:",
        "o": [
          "only in the army",
          "only in courts",
          "schools, homes and religious bodies",
          "only abroad"
        ],
        "a": 2,
        "e": "It should reach all parts of society.",
        "h": "Everywhere.",
        "yr": "SS3"
      },
      {
        "q": "Good governance promotes peace through:",
        "o": [
          "favouritism",
          "transparency and accountability",
          "secrecy",
          "impunity"
        ],
        "a": 1,
        "e": "Open and accountable government builds trust.",
        "h": "Trust.",
        "yr": "SS3"
      },
      {
        "q": "Peace is important in international relations because it:",
        "o": [
          "encourages cooperation among nations",
          "causes trade wars",
          "ends diplomacy",
          "increases arms races"
        ],
        "a": 0,
        "e": "Peaceful relations allow trade and joint action.",
        "h": "Cooperation.",
        "yr": "SS3"
      },
      {
        "q": "A country at peace is likely to:",
        "o": [
          "stop education",
          "have frequent strikes only",
          "attract investment and tourists",
          "lose investors"
        ],
        "a": 2,
        "e": "Stability attracts economic activity.",
        "h": "Positive effects.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is NOT a way of promoting peace?",
        "o": [
          "Hate speech",
          "Tolerance",
          "Justice",
          "Dialogue"
        ],
        "a": 0,
        "e": "Hate speech inflames divisions.",
        "h": "Pick the harmful option.",
        "yr": "SS3"
      },
      {
        "q": "The absence of war alone is called:",
        "o": [
          "total peace",
          "negative peace",
          "positive peace",
          "just peace"
        ],
        "a": 1,
        "e": "Negative peace is merely the absence of violence; positive peace includes justice and harmony.",
        "h": "Peace with no war but no justice.",
        "yr": "SS3"
      },
      {
        "q": "Peace can be strengthened by settling disputes through:",
        "o": [
          "violence",
          "threats",
          "boycott only",
          "dialogue and negotiation"
        ],
        "a": 3,
        "e": "Non-violent methods build lasting peace.",
        "h": "Talks.",
        "yr": "SS3"
      },
      {
        "q": "Peace promotes national integration by:",
        "o": [
          "encouraging unity among diverse peoples",
          "dividing people",
          "causing distrust",
          "ending cooperation"
        ],
        "a": 0,
        "e": "It fosters trust across groups.",
        "h": "Bringing people together.",
        "yr": "SS3"
      },
      {
        "q": "One role of the citizen in promoting peace is to:",
        "o": [
          "spread rumours",
          "discriminate",
          "obey the law and respect others' rights",
          "disobey the law"
        ],
        "a": 2,
        "e": "Every citizen contributes by lawful, respectful conduct.",
        "h": "Be law-abiding.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "The Interdependence of Nations and Globalization",
    "topicCode": "SS3-GOV-04",
    "module": "International Relations",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">No country can meet all its needs alone. <span class=\"learn-keyword\">Interdependence</span> is the mutual reliance of nations on one another for goods, services, technology, security and ideas. <span class=\"learn-keyword\">Globalization</span> is the growing integration of the world's economies, cultures and politics.</div><h3 class=\"learn-subheading\">1. Reasons for Interdependence</h3><ul style=\"margin-left:20px;\"><li>Uneven distribution of natural resources (e.g. Nigeria has crude oil but imports machinery).</li><li>Differences in technology and skills.</li><li>Need for security and collective defence.</li><li>Need for markets for exports and sources of imports.</li><li>Shared global problems such as disease, climate change and terrorism.</li></ul><h3 class=\"learn-subheading\">2. Nigeria's International Relations</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Area</th><th>Examples</th></tr></thead><tbody><tr><td>Economic</td><td>Oil exports, OPEC membership, trade agreements, loans and foreign investment</td></tr><tr><td>Political</td><td>Membership of the UN, AU, ECOWAS and the Commonwealth; diplomatic missions</td></tr><tr><td>Cultural</td><td>Festivals, FESTAC 77, exchange of music, film and education</td></tr><tr><td>Social</td><td>Student exchange, health cooperation, humanitarian assistance</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Advantages and Disadvantages of Interaction Among Nations</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Advantages</th><th>Disadvantages</th></tr></thead><tbody><tr><td>Access to goods, technology and expertise</td><td>Dependence on stronger nations</td></tr><tr><td>Larger markets and foreign exchange</td><td>Dumping of foreign goods harms local industries</td></tr><tr><td>Peace and security through cooperation</td><td>Loss of some sovereignty; foreign interference</td></tr><tr><td>Cultural exchange</td><td>Erosion of local culture and values</td></tr><tr><td>Collective solutions to global problems</td><td>Spread of diseases, crime and terrorism</td></tr></tbody></table></div>",
    "questions": [
      {
        "q": "Interdependence of nations means:",
        "o": [
          "nations refuse to trade",
          "countries are totally self-sufficient",
          "a single country rules others",
          "countries rely on one another for goods, services and ideas"
        ],
        "a": 3,
        "e": "No country has everything it needs.",
        "h": "Mutual reliance.",
        "yr": "SS3"
      },
      {
        "q": "One reason nations depend on each other is:",
        "o": [
          "lack of transport in the world",
          "uneven distribution of natural resources",
          "absence of trade",
          "equal resources everywhere"
        ],
        "a": 1,
        "e": "Resources are unevenly spread.",
        "h": "Oil is not found everywhere.",
        "yr": "SS3"
      },
      {
        "q": "Globalization refers to:",
        "o": [
          "only political alliances",
          "local trade",
          "isolation of nations",
          "the increasing integration of the world's economies, cultures and politics"
        ],
        "a": 3,
        "e": "It is the growing worldwide connectedness.",
        "h": "A 'global village'.",
        "yr": "SS3"
      },
      {
        "q": "An economic aspect of Nigeria's international relations is:",
        "o": [
          "student exchange",
          "sports festivals",
          "language teaching",
          "oil exports and OPEC membership"
        ],
        "a": 3,
        "e": "Oil trade is a major economic link.",
        "h": "Money and trade.",
        "yr": "SS3"
      },
      {
        "q": "FESTAC 77 is an example of Nigeria's:",
        "o": [
          "political alliances",
          "cultural relations",
          "economic sanctions",
          "military relations"
        ],
        "a": 1,
        "e": "The Festival of Black and African Arts and Culture, 1977, was a cultural event.",
        "h": "Arts.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's membership of the UN and AU is an example of:",
        "o": [
          "social welfare",
          "cultural relations",
          "political relations",
          "commercial trade"
        ],
        "a": 2,
        "e": "Membership of international bodies is political.",
        "h": "Diplomacy.",
        "yr": "SS3"
      },
      {
        "q": "An advantage of interaction among nations is:",
        "o": [
          "spread of disease",
          "access to technology and expertise",
          "erosion of culture",
          "dumping of goods"
        ],
        "a": 1,
        "e": "Nations share knowledge and skills.",
        "h": "Positive effect.",
        "yr": "SS3"
      },
      {
        "q": "A disadvantage of interaction among nations is:",
        "o": [
          "larger markets",
          "cultural exchange",
          "dumping of foreign goods that harms local industries",
          "peace through cooperation"
        ],
        "a": 2,
        "e": "Cheap imports can destroy local producers.",
        "h": "Negative effect.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a social aspect of international relations?",
        "o": [
          "Military pacts",
          "Student exchange programmes",
          "Oil price fixing",
          "Tariffs"
        ],
        "a": 1,
        "e": "Exchange of students and health cooperation are social links.",
        "h": "People-to-people.",
        "yr": "SS3"
      },
      {
        "q": "Interdependence encourages nations to:",
        "o": [
          "isolate themselves",
          "go to war",
          "stop trade",
          "cooperate and maintain peaceful relations"
        ],
        "a": 3,
        "e": "Mutual need promotes cooperation.",
        "h": "They need each other.",
        "yr": "SS3"
      },
      {
        "q": "Globalization has been helped most by:",
        "o": [
          "closing borders",
          "isolationist policies",
          "ban on trade",
          "advances in ICT and transportation"
        ],
        "a": 3,
        "e": "Internet and fast transport link the world.",
        "h": "Technology.",
        "yr": "SS3"
      },
      {
        "q": "A negative effect of globalization on culture is:",
        "o": [
          "preservation of all traditions",
          "strengthening of local languages",
          "erosion of local values",
          "reduction of foreign influence"
        ],
        "a": 2,
        "e": "Foreign culture can overshadow local culture.",
        "h": "Culture loss.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's imports of machinery show:",
        "o": [
          "dependence on other countries for technology",
          "self-sufficiency",
          "closed economy",
          "absence of trade"
        ],
        "a": 0,
        "e": "Nigeria relies on foreign technology for machines.",
        "h": "Trade need.",
        "yr": "SS3"
      },
      {
        "q": "Collective security is an advantage of interaction because it:",
        "o": [
          "causes war",
          "weakens sovereignty entirely",
          "protects nations through cooperation",
          "ends alliances"
        ],
        "a": 2,
        "e": "Nations defend each other via alliances and organisations.",
        "h": "Security.",
        "yr": "SS3"
      },
      {
        "q": "Which is a disadvantage of interdependence?",
        "o": [
          "Peace",
          "Possible loss of some sovereignty",
          "Technology transfer",
          "Wider markets"
        ],
        "a": 1,
        "e": "Powerful states may influence weaker ones.",
        "h": "Loss of independence.",
        "yr": "SS3"
      },
      {
        "q": "The spread of diseases across borders is a result of:",
        "o": [
          "closed borders",
          "increased international contact",
          "absence of travel",
          "isolation"
        ],
        "a": 1,
        "e": "More movement of people spreads diseases.",
        "h": "Travel.",
        "yr": "SS3"
      },
      {
        "q": "Which is NOT an area of Nigeria's international relations?",
        "o": [
          "Cultural",
          "Economic",
          "Isolation",
          "Political"
        ],
        "a": 2,
        "e": "Nigeria actively interacts with other nations.",
        "h": "Pick the odd one.",
        "yr": "SS3"
      },
      {
        "q": "A country that has oil but imports refined goods illustrates:",
        "o": [
          "interdependence",
          "isolationism",
          "autarky",
          "self-reliance"
        ],
        "a": 0,
        "e": "It relies on others for what it lacks.",
        "h": "Mutual need.",
        "yr": "SS3"
      },
      {
        "q": "Foreign investment in Nigeria is an example of:",
        "o": [
          "economic interaction between nations",
          "political boycott",
          "cultural isolation",
          "military conflict"
        ],
        "a": 0,
        "e": "Capital flows across borders.",
        "h": "Business.",
        "yr": "SS3"
      },
      {
        "q": "Global problems like climate change require:",
        "o": [
          "individual action only",
          "isolation",
          "international cooperation",
          "war"
        ],
        "a": 2,
        "e": "No country can solve them alone.",
        "h": "Working together.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Nigeria's Foreign Policy I",
    "topicCode": "SS3-GOV-05",
    "module": "International Relations",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">Foreign policy</span> is the set of principles, objectives and strategies a country uses in its relations with other countries and international bodies. It is aimed at protecting and promoting the national interest.</div><h3 class=\"learn-subheading\">1. Factors that Influence Nigeria's Foreign Policy</h3><ul style=\"margin-left:20px;\"><li>Geographical location, size and population (Nigeria is the most populous country in Africa).</li><li>Economic conditions and natural resources, especially oil.</li><li>Historical experiences such as colonialism and the civil war.</li><li>Military and technological capability.</li><li>Political ideology and the type of government in power.</li><li>The international environment, including the Cold War and global alliances.</li><li>Domestic public opinion and the interests of leaders.</li></ul><h3 class=\"learn-subheading\">2. Principles of Nigeria's Foreign Policy</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Principle</th><th>Meaning</th></tr></thead><tbody><tr><td>Africa as the centrepiece</td><td>Priority is given to African affairs</td></tr><tr><td>Non-alignment</td><td>Not joining any power bloc</td></tr><tr><td>Non-interference</td><td>Respect for the internal affairs of other states</td></tr><tr><td>Respect for sovereignty and territorial integrity</td><td>Every state is equal</td></tr><tr><td>Peaceful settlement of disputes</td><td>Use of dialogue, not force</td></tr><tr><td>Support for decolonisation and against racism</td><td>Opposition to apartheid and colonial rule</td></tr><tr><td>Promotion of international cooperation</td><td>Support for the UN, AU and ECOWAS</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Objectives of Nigeria's Foreign Policy</h3><ul style=\"margin-left:20px;\"><li>Protect the sovereignty and territorial integrity of Nigeria.</li><li>Promote the well-being and dignity of Nigerians at home and abroad.</li><li>Promote African unity and economic integration.</li><li>Promote world peace and international cooperation.</li><li>Attract foreign investment and technology for development.</li><li>Oppose colonialism, apartheid and all forms of racial discrimination.</li></ul>",
    "questions": [
      {
        "q": "Foreign policy is:",
        "o": [
          "the constitution",
          "a set of principles and objectives guiding a country's relations with others",
          "a domestic election plan",
          "a country's tax law"
        ],
        "a": 1,
        "e": "It guides how a state acts internationally.",
        "h": "External relations.",
        "yr": "SS3"
      },
      {
        "q": "Which factor influences Nigeria's foreign policy?",
        "o": [
          "Only its rainfall",
          "Only its colours of flag",
          "Its national anthem",
          "Its size, population and location"
        ],
        "a": 3,
        "e": "Nigeria's demographic and geographic weight shapes policy.",
        "h": "Big country.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's oil wealth influences foreign policy by:",
        "o": [
          "ending diplomacy",
          "causing isolation",
          "preventing trade",
          "shaping economic relations and OPEC membership"
        ],
        "a": 3,
        "e": "Oil determines many trade and diplomatic ties.",
        "h": "Economy.",
        "yr": "SS3"
      },
      {
        "q": "Which historical experience shaped Nigeria's foreign policy?",
        "o": [
          "The Gulf War only",
          "Colonial rule",
          "Ancient Rome",
          "The Industrial Revolution in Nigeria"
        ],
        "a": 1,
        "e": "Nigeria's colonial past shapes anti-colonial stance.",
        "h": "Freedom struggle.",
        "yr": "SS3"
      },
      {
        "q": "Africa as the centrepiece means:",
        "o": [
          "only Europe matters",
          "priority is given to African affairs",
          "Africa is ignored",
          "trade with Asia only"
        ],
        "a": 1,
        "e": "Nigeria's first priority is Africa.",
        "h": "Focus.",
        "yr": "SS3"
      },
      {
        "q": "Non-alignment means:",
        "o": [
          "joining NATO",
          "not joining any major power bloc",
          "military alliance with the USA",
          "joining Warsaw Pact"
        ],
        "a": 1,
        "e": "It avoids taking sides in Cold War blocs.",
        "h": "Neutral.",
        "yr": "SS3"
      },
      {
        "q": "Non-interference in the internal affairs of other states is a:",
        "o": [
          "principle of Nigeria's foreign policy",
          "breach of policy",
          "tax policy",
          "military objective"
        ],
        "a": 0,
        "e": "Nigeria respects other states' internal affairs.",
        "h": "Respect.",
        "yr": "SS3"
      },
      {
        "q": "Respect for sovereignty and territorial integrity means:",
        "o": [
          "invading neighbours",
          "annexing land",
          "recognising each state's independence and boundaries",
          "ignoring borders"
        ],
        "a": 2,
        "e": "Each state's independence and borders must be respected.",
        "h": "Borders.",
        "yr": "SS3"
      },
      {
        "q": "Which is an objective of Nigeria's foreign policy?",
        "o": [
          "Promotion of African unity and integration",
          "Promoting war",
          "Supporting colonialism",
          "Encouraging racism"
        ],
        "a": 0,
        "e": "Nigeria promotes African unity and cooperation.",
        "h": "Positive aim.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's opposition to apartheid shows its commitment to:",
        "o": [
          "imperialism",
          "the fight against racism and colonialism",
          "racial discrimination",
          "military alliances"
        ],
        "a": 1,
        "e": "It supported liberation struggles in southern Africa.",
        "h": "Anti-racism.",
        "yr": "SS3"
      },
      {
        "q": "Protecting the well-being of Nigerians abroad is:",
        "o": [
          "not the government's concern",
          "an objective of foreign policy",
          "against the constitution",
          "only a private matter"
        ],
        "a": 1,
        "e": "Citizens' welfare abroad is a foreign policy goal.",
        "h": "Diaspora.",
        "yr": "SS3"
      },
      {
        "q": "Peaceful settlement of disputes means:",
        "o": [
          "fighting wars",
          "using dialogue and negotiation, not force",
          "invasion",
          "threats"
        ],
        "a": 1,
        "e": "Nigeria prefers peaceful means.",
        "h": "Dialogue.",
        "yr": "SS3"
      },
      {
        "q": "The Cold War is an example of an:",
        "o": [
          "international environment factor",
          "domestic crisis",
          "religious factor",
          "local election"
        ],
        "a": 0,
        "e": "The superpower rivalry shaped foreign policy choices.",
        "h": "Global context.",
        "yr": "SS3"
      },
      {
        "q": "The type of government in power influences foreign policy because:",
        "o": [
          "leaders shape priorities and approaches",
          "only judges decide it",
          "it has no effect",
          "policy never changes"
        ],
        "a": 0,
        "e": "Different regimes emphasise different aims.",
        "h": "Leadership.",
        "yr": "SS3"
      },
      {
        "q": "Foreign policy aims mainly to protect:",
        "o": [
          "national interest",
          "foreign interest",
          "company interest",
          "private interest only"
        ],
        "a": 0,
        "e": "It promotes and defends the national interest.",
        "h": "Nation first.",
        "yr": "SS3"
      },
      {
        "q": "Which is NOT a principle of Nigeria's foreign policy?",
        "o": [
          "Non-alignment",
          "Interference in other states' affairs",
          "Africa as centrepiece",
          "Non-interference"
        ],
        "a": 1,
        "e": "Nigeria practises non-interference.",
        "h": "Find the contradiction.",
        "yr": "SS3"
      },
      {
        "q": "Attracting foreign investment is an objective because it:",
        "o": [
          "reduces jobs",
          "ruins industries",
          "stops trade",
          "supports economic development"
        ],
        "a": 3,
        "e": "Investment brings capital and technology.",
        "h": "Development.",
        "yr": "SS3"
      },
      {
        "q": "Military capability affects foreign policy by:",
        "o": [
          "removing sovereignty",
          "preventing trade",
          "making it irrelevant",
          "influencing a country's ability to defend interests and take part in peacekeeping"
        ],
        "a": 3,
        "e": "A stronger military enables greater international role.",
        "h": "Power.",
        "yr": "SS3"
      },
      {
        "q": "Public opinion affects foreign policy because:",
        "o": [
          "it is ignored always",
          "only foreigners decide",
          "it is illegal",
          "governments consider the views of citizens"
        ],
        "a": 3,
        "e": "Leaders often respond to domestic opinion.",
        "h": "Citizens.",
        "yr": "SS3"
      },
      {
        "q": "Support for the United Nations and AU shows Nigeria's:",
        "o": [
          "militarism",
          "commitment to international cooperation",
          "isolation",
          "expansionism"
        ],
        "a": 1,
        "e": "It supports multilateral cooperation.",
        "h": "Working together.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Nigeria's Foreign Policy II",
    "topicCode": "SS3-GOV-06",
    "module": "International Relations",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">Since independence in 1960, <span class=\"learn-keyword\">Africa as the centrepiece</span> and <span class=\"learn-keyword\">non-alignment</span> have been the two most important features of Nigeria's foreign policy.</div><h3 class=\"learn-subheading\">1. Origin and Justification of Africa as the Centrepiece</h3><p class=\"learn-p\">Prime Minister Abubakar Tafawa Balewa announced in 1960 that Africa would be the centrepiece of Nigeria's foreign policy. Reasons include:</p><ul style=\"margin-left:20px;\"><li>Nigeria's large size, population and resources give it a leadership responsibility.</li><li>Shared history of colonialism and the need for African unity.</li><li>Nigeria's security and prosperity depend on a stable, developing neighbourhood.</li><li>The desire to end colonialism and apartheid in Africa.</li></ul><h3 class=\"learn-subheading\">2. Ways Nigeria Has Demonstrated Africa as the Centrepiece</h3><ul style=\"margin-left:20px;\"><li>Leading role in founding the OAU (1963) and ECOWAS (1975).</li><li>Opposition to apartheid in South Africa; support for liberation movements in Angola, Mozambique, Zimbabwe and Namibia.</li><li>Peacekeeping in Congo, Liberia, Sierra Leone, Chad and Darfur (through ECOMOG and the AU).</li><li>Technical Aid Corps (TAC) programme, sending professionals to African countries.</li><li>Financial aid and support for neighbouring states.</li></ul><h3 class=\"learn-subheading\">3. Non-Alignment</h3><p class=\"learn-p\">Non-alignment means not joining any major power bloc, such as NATO in the West or the Warsaw Pact in the East, during the Cold War, and taking positions on issues on their merits.</p><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Aims and Objectives</th><th>Problems</th></tr></thead><tbody><tr><td>Preserve independence and sovereignty</td><td>Pressure from powerful states to take sides</td></tr><tr><td>Avoid entanglement in others' wars</td><td>Difficulty in receiving aid from both blocs</td></tr><tr><td>Freedom to trade and cooperate with all states</td><td>Accusations of leaning towards one bloc, as in the 1970s</td></tr><tr><td>Focus on national development</td><td>Cold War ended, so the meaning of the policy has changed</td></tr></tbody></table></div>",
    "questions": [
      {
        "q": "Who announced Africa as the centrepiece of Nigeria's foreign policy in 1960?",
        "o": [
          "Nnamdi Azikiwe",
          "Olusegun Obasanjo",
          "Yakubu Gowon",
          "Abubakar Tafawa Balewa"
        ],
        "a": 3,
        "e": "Prime Minister Balewa declared it at independence.",
        "h": "Nigeria's first Prime Minister.",
        "yr": "SS3"
      },
      {
        "q": "Which is a reason for adopting Africa as the centrepiece?",
        "o": [
          "Nigeria's size and resources give it a leadership role",
          "Nigeria has no African neighbours",
          "Nigeria wants to leave Africa",
          "Africa has no problems"
        ],
        "a": 0,
        "e": "Nigeria's size, population and resources carry responsibility.",
        "h": "Big brother role.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's support for liberation in southern Africa shows:",
        "o": [
          "Africa as the centrepiece in practice",
          "support for apartheid",
          "neglect of Africa",
          "isolationism"
        ],
        "a": 0,
        "e": "Nigeria backed liberation movements in Angola, Mozambique, Zimbabwe and Namibia.",
        "h": "Freedom fighting.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's peacekeeping missions in Liberia and Sierra Leone were carried out under:",
        "o": [
          "SEATO",
          "NATO",
          "ECOMOG",
          "OPEC"
        ],
        "a": 2,
        "e": "ECOWAS Monitoring Group led by Nigerian troops.",
        "h": "An ECOWAS force.",
        "yr": "SS3"
      },
      {
        "q": "The Technical Aid Corps (TAC) scheme sends Nigerian:",
        "o": [
          "professionals to assist other developing countries",
          "tourists to Europe",
          "soldiers to invade neighbours",
          "traders to America"
        ],
        "a": 0,
        "e": "TAC provides skilled personnel to African, Caribbean and Pacific states.",
        "h": "Skills for help.",
        "yr": "SS3"
      },
      {
        "q": "Non-alignment means Nigeria will:",
        "o": [
          "take positions on issues on their merits",
          "always follow the USA",
          "always follow the USSR",
          "join every alliance"
        ],
        "a": 0,
        "e": "It refuses to be tied to any bloc.",
        "h": "Independent judgement.",
        "yr": "SS3"
      },
      {
        "q": "Which Cold War alliance did non-aligned countries avoid?",
        "o": [
          "The AU",
          "The UN",
          "The Commonwealth",
          "NATO and the Warsaw Pact"
        ],
        "a": 3,
        "e": "Non-aligned states stayed outside the two military blocs.",
        "h": "East–West blocs.",
        "yr": "SS3"
      },
      {
        "q": "An aim of the non-alignment policy is to:",
        "o": [
          "join a superpower",
          "preserve independence and sovereignty",
          "start wars",
          "give up sovereignty"
        ],
        "a": 1,
        "e": "It keeps the country free from foreign domination.",
        "h": "Independence.",
        "yr": "SS3"
      },
      {
        "q": "A problem of non-alignment is:",
        "o": [
          "full freedom in all cases",
          "pressure from powerful states to take sides",
          "too much peace",
          "lack of trade partners"
        ],
        "a": 1,
        "e": "Big powers often push others to align.",
        "h": "Pressure.",
        "yr": "SS3"
      },
      {
        "q": "The end of the Cold War affected non-alignment by:",
        "o": [
          "making it stronger",
          "ending the UN",
          "changing its meaning and relevance",
          "banning trade"
        ],
        "a": 2,
        "e": "With one bloc gone, the policy's original meaning shifted.",
        "h": "Times changed.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's opposition to apartheid was shown by:",
        "o": [
          "trading freely with South Africa",
          "silence",
          "military support to Pretoria",
          "leading sanctions and supporting the ANC and liberation groups"
        ],
        "a": 3,
        "e": "Nigeria was a front-line supporter of anti-apartheid efforts.",
        "h": "Anti-apartheid.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's role in founding ECOWAS in 1975 supports:",
        "o": [
          "colonialism",
          "isolationism",
          "African economic integration",
          "European integration"
        ],
        "a": 2,
        "e": "It was a leading founder.",
        "h": "Regional bloc.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a way Nigeria has shown Africa as centrepiece?",
        "o": [
          "Ignoring African crises",
          "Sending peacekeepers to conflict zones in Africa",
          "Withdrawing from the AU",
          "Supporting colonial rule"
        ],
        "a": 1,
        "e": "Nigeria has taken part in UN and AU peacekeeping.",
        "h": "Action for peace.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's actions in Congo in the 1960s were part of:",
        "o": [
          "NATO operations",
          "UN peacekeeping operations",
          "Commonwealth Games",
          "OPEC meetings"
        ],
        "a": 1,
        "e": "Nigerian troops served in the Congo under the UN.",
        "h": "Early peacekeeping.",
        "yr": "SS3"
      },
      {
        "q": "Non-alignment enables a country to:",
        "o": [
          "trade only with one bloc",
          "trade and cooperate with all states",
          "fight all blocs",
          "avoid all trade"
        ],
        "a": 1,
        "e": "It keeps options open.",
        "h": "Freedom.",
        "yr": "SS3"
      },
      {
        "q": "The centrepiece policy seeks mainly to:",
        "o": [
          "favour Europe",
          "promote African unity and development",
          "favour Asia",
          "isolate Africa"
        ],
        "a": 1,
        "e": "Its goal is African cooperation and progress.",
        "h": "Continental focus.",
        "yr": "SS3"
      },
      {
        "q": "One justification for Africa as centrepiece is that Nigeria's security depends on:",
        "o": [
          "foreign navies",
          "far-away wars",
          "a stable neighbourhood",
          "space programmes"
        ],
        "a": 2,
        "e": "Instability in neighbours threatens Nigeria.",
        "h": "Neighbours.",
        "yr": "SS3"
      },
      {
        "q": "Apartheid was a system of:",
        "o": [
          "military alliance",
          "racial discrimination in South Africa",
          "religious rule",
          "economic union"
        ],
        "a": 1,
        "e": "It separated races and denied rights to non-whites.",
        "h": "South Africa's past.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's policy of non-alignment was tested in the 1970s when it was accused of:",
        "o": [
          "neutral peace",
          "leaning toward one bloc",
          "isolationism",
          "full alignment with West"
        ],
        "a": 1,
        "e": "Critics said certain actions favoured a bloc.",
        "h": "Accusation.",
        "yr": "SS3"
      },
      {
        "q": "The main opponent of colonialism among Nigeria's foreign policy goals shows:",
        "o": [
          "neutrality",
          "support for empires",
          "solidarity with oppressed African peoples",
          "indifference"
        ],
        "a": 2,
        "e": "It opposes all forms of colonial domination.",
        "h": "Anti-colonial.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "International Organisation: United Nations Organisation (UNO)",
    "topicCode": "SS3-GOV-07",
    "module": "International Organisations",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">The <span class=\"learn-keyword\">United Nations Organisation (UNO)</span> is a global body of sovereign states formed in 1945, after the Second World War, to maintain international peace and security. It replaced the League of Nations and has its headquarters in New York.</div><h3 class=\"learn-subheading\">1. Origin</h3><p class=\"learn-p\">The UN Charter was signed in San Francisco in June 1945 and the organisation came into being on 24 October 1945 with 51 original members. Nigeria joined in 1960 as its 99th member.</p><h3 class=\"learn-subheading\">2. Principal Organs</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Organ</th><th>Function</th></tr></thead><tbody><tr><td>General Assembly</td><td>All members; discusses issues and passes the budget</td></tr><tr><td>Security Council</td><td>Primary responsibility for peace and security; 5 permanent members with veto (USA, UK, France, Russia, China) and 10 non-permanent</td></tr><tr><td>Economic and Social Council (ECOSOC)</td><td>Economic, social and humanitarian cooperation</td></tr><tr><td>Trusteeship Council</td><td>Supervised trust territories (suspended operations in 1994)</td></tr><tr><td>International Court of Justice (ICJ)</td><td>Settles legal disputes between states; sits at The Hague</td></tr><tr><td>Secretariat</td><td>Administration, headed by the Secretary-General</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Aims and Objectives</h3><ul style=\"margin-left:20px;\"><li>Maintain international peace and security.</li><li>Develop friendly relations among nations.</li><li>Promote cooperation in solving economic, social, cultural and humanitarian problems.</li><li>Promote respect for human rights and fundamental freedoms.</li></ul><h3 class=\"learn-subheading\">4. Achievements and Problems</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Achievements</th><th>Problems</th></tr></thead><tbody><tr><td>Peacekeeping and conflict prevention</td><td>The veto power of the five permanent members</td></tr><tr><td>Decolonisation of many territories</td><td>Shortage of funds and unpaid contributions</td></tr><tr><td>Specialised agencies: WHO, UNESCO, UNICEF, FAO, ILO</td><td>Inability to enforce decisions against powerful states</td></tr><tr><td>Humanitarian aid and refugee support</td><td>Domination by big powers; slow response to crises</td></tr></tbody></table></div>",
    "questions": [
      {
        "q": "The UN was formed in:",
        "o": [
          "1960",
          "1919",
          "1963",
          "1945"
        ],
        "a": 3,
        "e": "It came into being on 24 October 1945.",
        "h": "After WWII.",
        "yr": "SS3"
      },
      {
        "q": "The UN replaced the:",
        "o": [
          "OAU",
          "League of Nations",
          "Commonwealth",
          "EU"
        ],
        "a": 1,
        "e": "The League failed to prevent WWII.",
        "h": "Earlier organisation.",
        "yr": "SS3"
      },
      {
        "q": "The headquarters of the UN is in:",
        "o": [
          "New York",
          "Addis Ababa",
          "Geneva",
          "London"
        ],
        "a": 0,
        "e": "The UN headquarters is in New York City.",
        "h": "USA.",
        "yr": "SS3"
      },
      {
        "q": "How many permanent members does the Security Council have?",
        "o": [
          "Five",
          "Ten",
          "Three",
          "Fifteen"
        ],
        "a": 0,
        "e": "USA, UK, France, Russia and China hold permanent seats with veto.",
        "h": "Five with veto.",
        "yr": "SS3"
      },
      {
        "q": "The power of a permanent member to block a Security Council resolution is called:",
        "o": [
          "ballot",
          "amendment",
          "veto",
          "referendum"
        ],
        "a": 2,
        "e": "A single negative vote by a permanent member blocks a resolution.",
        "h": "It means 'I forbid'.",
        "yr": "SS3"
      },
      {
        "q": "The principal judicial organ of the UN is the:",
        "o": [
          "Secretariat",
          "ECOSOC",
          "International Court of Justice",
          "Security Council"
        ],
        "a": 2,
        "e": "The ICJ settles legal disputes between states.",
        "h": "Court at The Hague.",
        "yr": "SS3"
      },
      {
        "q": "The UN organ headed by the Secretary-General is the:",
        "o": [
          "Security Council",
          "Trusteeship Council",
          "Secretariat",
          "General Assembly"
        ],
        "a": 2,
        "e": "The Secretariat handles administration.",
        "h": "Administrative arm.",
        "yr": "SS3"
      },
      {
        "q": "The organ in which all member states are represented is the:",
        "o": [
          "General Assembly",
          "Security Council",
          "ICJ",
          "Trusteeship Council"
        ],
        "a": 0,
        "e": "Each member has one vote in the Assembly.",
        "h": "All members.",
        "yr": "SS3"
      },
      {
        "q": "Which organ has primary responsibility for maintaining peace and security?",
        "o": [
          "Security Council",
          "ECOSOC",
          "General Assembly",
          "Secretariat"
        ],
        "a": 0,
        "e": "The Security Council can authorise sanctions and force.",
        "h": "Peace and security.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria became a member of the UN in:",
        "o": [
          "1945",
          "1963",
          "1960",
          "1975"
        ],
        "a": 2,
        "e": "Nigeria joined shortly after independence.",
        "h": "Independence year.",
        "yr": "SS3"
      },
      {
        "q": "An aim of the UN is to:",
        "o": [
          "encourage colonialism",
          "maintain international peace and security",
          "promote arms races",
          "increase tariffs"
        ],
        "a": 1,
        "e": "This is its primary purpose.",
        "h": "Peace.",
        "yr": "SS3"
      },
      {
        "q": "Which is an achievement of the UN?",
        "o": [
          "Ending all wars",
          "Uniting all nations into one state",
          "Abolishing poverty",
          "Peacekeeping and support for decolonisation"
        ],
        "a": 3,
        "e": "It has helped end conflicts and supported independence.",
        "h": "Positive record.",
        "yr": "SS3"
      },
      {
        "q": "WHO, UNESCO and UNICEF are:",
        "o": [
          "military alliances",
          "UN specialised agencies",
          "trade unions",
          "regional banks"
        ],
        "a": 1,
        "e": "They work on health, education and children.",
        "h": "Under the UN.",
        "yr": "SS3"
      },
      {
        "q": "A problem of the UN is:",
        "o": [
          "unanimous agreement",
          "too many peacekeepers",
          "excessive funding",
          "the veto power of the permanent members"
        ],
        "a": 3,
        "e": "The veto can block action on major crises.",
        "h": "A voting issue.",
        "yr": "SS3"
      },
      {
        "q": "Another problem of the UN is:",
        "o": [
          "full compliance by all states",
          "surplus funds",
          "shortage of funds due to unpaid contributions",
          "no member disputes"
        ],
        "a": 2,
        "e": "Some members fail to pay dues.",
        "h": "Money.",
        "yr": "SS3"
      },
      {
        "q": "The UN Charter was signed in:",
        "o": [
          "Geneva",
          "Lagos",
          "San Francisco",
          "Paris"
        ],
        "a": 2,
        "e": "It was signed in June 1945.",
        "h": "US west coast.",
        "yr": "SS3"
      },
      {
        "q": "The UN's Trusteeship Council supervised:",
        "o": [
          "oil prices",
          "trust territories",
          "refugees only",
          "peacekeeping"
        ],
        "a": 1,
        "e": "It oversaw territories heading to independence.",
        "h": "Trust territories.",
        "yr": "SS3"
      },
      {
        "q": "The economic and social work of the UN is coordinated by:",
        "o": [
          "Security Council",
          "ECOSOC",
          "Secretariat",
          "ICJ"
        ],
        "a": 1,
        "e": "Economic and Social Council.",
        "h": "Economic body.",
        "yr": "SS3"
      },
      {
        "q": "How many original members did the UN have in 1945?",
        "o": [
          "193",
          "99",
          "15",
          "51"
        ],
        "a": 3,
        "e": "Fifty-one states were original members.",
        "h": "Just over fifty.",
        "yr": "SS3"
      },
      {
        "q": "The UN promotes human rights through:",
        "o": [
          "the Treaty of Versailles",
          "the Berlin Treaty",
          "the Treaty of Lagos",
          "the Universal Declaration of Human Rights"
        ],
        "a": 3,
        "e": "Adopted in 1948.",
        "h": "Rights.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "International Organisation: Organisation of African Unity (OAU) / African Union (AU)",
    "topicCode": "SS3-GOV-08",
    "module": "International Organisations",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">The <span class=\"learn-keyword\">Organisation of African Unity (OAU)</span> was founded in <span class=\"learn-keyword\">1963</span> in Addis Ababa, Ethiopia, and was replaced by the <span class=\"learn-keyword\">African Union (AU)</span>, launched in 2002 in Durban, South Africa.</div><h3 class=\"learn-subheading\">1. Origin</h3><p class=\"learn-p\">After many African states won independence, leaders debated how to unite. The Casablanca group (radical, favouring quick political union) and the Monrovia group (moderate, favouring gradual cooperation, including Nigeria) compromised, and 32 independent states signed the OAU Charter on 25 May 1963. The AU replaced the OAU in 2002 to deepen integration; its headquarters is in Addis Ababa.</p><h3 class=\"learn-subheading\">2. Aims and Objectives of OAU/AU</h3><ul style=\"margin-left:20px;\"><li>Promote unity and solidarity among African states.</li><li>Defend sovereignty and territorial integrity.</li><li>Eradicate colonialism and apartheid.</li><li>Promote cooperation for development.</li><li>Promote peace, security and stability.</li><li>AU: promote democracy, good governance and human rights, and the Pan-African Parliament.</li></ul><h3 class=\"learn-subheading\">3. Achievements and Problems</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Achievements</th><th>Problems</th></tr></thead><tbody><tr><td>Helped end colonial rule and apartheid</td><td>Lack of funds and reliance on foreign donors</td></tr><tr><td>Mediation and peacekeeping in conflicts</td><td>Interference in members' internal affairs is discouraged by the charter</td></tr><tr><td>Promoted African cooperation and a common voice at the UN</td><td>Ethnic and boundary disputes; military coups</td></tr><tr><td>AU: African Peace and Security Architecture; NEPAD; African Court</td><td>Political instability and poverty; slow implementation of decisions</td></tr></tbody></table></div>",
    "questions": [
      {
        "q": "The OAU was founded in:",
        "o": [
          "1963",
          "2002",
          "1945",
          "1975"
        ],
        "a": 0,
        "e": "It was established on 25 May 1963.",
        "h": "After many independences.",
        "yr": "SS3"
      },
      {
        "q": "The OAU was founded in:",
        "o": [
          "Addis Ababa, Ethiopia",
          "Cairo, Egypt",
          "Lagos, Nigeria",
          "Accra, Ghana"
        ],
        "a": 0,
        "e": "The charter was signed in Addis Ababa.",
        "h": "Ethiopian capital.",
        "yr": "SS3"
      },
      {
        "q": "The African Union replaced the OAU in:",
        "o": [
          "2002",
          "1999",
          "1975",
          "1963"
        ],
        "a": 0,
        "e": "The AU was launched in Durban, South Africa, in 2002.",
        "h": "Twenty-first century.",
        "yr": "SS3"
      },
      {
        "q": "The headquarters of the African Union is in:",
        "o": [
          "Addis Ababa",
          "Abuja",
          "Pretoria",
          "Nairobi"
        ],
        "a": 0,
        "e": "It is based in Addis Ababa, Ethiopia.",
        "h": "Same as OAU.",
        "yr": "SS3"
      },
      {
        "q": "The group that favoured gradual cooperation among African states was the:",
        "o": [
          "Warsaw group",
          "Casablanca group",
          "Monrovia group",
          "Brazzaville group only"
        ],
        "a": 2,
        "e": "Nigeria was a member of the Monrovia group.",
        "h": "Moderates.",
        "yr": "SS3"
      },
      {
        "q": "The group that favoured quick political union was the:",
        "o": [
          "Commonwealth group",
          "Monrovia group",
          "Casablanca group",
          "Lagos group"
        ],
        "a": 2,
        "e": "Led by Ghana's Nkrumah.",
        "h": "Radicals.",
        "yr": "SS3"
      },
      {
        "q": "An aim of the OAU was to:",
        "o": [
          "increase colonial power",
          "promote wars",
          "eradicate colonialism and apartheid in Africa",
          "support colonialism"
        ],
        "a": 2,
        "e": "This was among its main goals.",
        "h": "Freedom.",
        "yr": "SS3"
      },
      {
        "q": "An aim of the OAU/AU is to:",
        "o": [
          "divide Africa",
          "support dictatorships",
          "promote unity and solidarity among African states",
          "end trade"
        ],
        "a": 2,
        "e": "African unity is central.",
        "h": "Unity.",
        "yr": "SS3"
      },
      {
        "q": "An additional aim of the AU is to:",
        "o": [
          "ignore human rights",
          "encourage coups",
          "promote democracy, good governance and human rights",
          "promote military rule"
        ],
        "a": 2,
        "e": "The AU emphasises democratic values.",
        "h": "Governance.",
        "yr": "SS3"
      },
      {
        "q": "One achievement of the OAU was:",
        "o": [
          "stopping all wars",
          "helping to end colonial rule and apartheid",
          "creating one currency",
          "removing all borders"
        ],
        "a": 1,
        "e": "It supported liberation movements.",
        "h": "Decolonisation.",
        "yr": "SS3"
      },
      {
        "q": "An AU achievement is the:",
        "o": [
          "end of all conflicts",
          "common army",
          "single passport for all",
          "African Peace and Security Architecture"
        ],
        "a": 3,
        "e": "It is a framework for conflict prevention and management.",
        "h": "Peace framework.",
        "yr": "SS3"
      },
      {
        "q": "A problem confronting the OAU/AU is:",
        "o": [
          "no conflicts",
          "lack of funds and reliance on donors",
          "total unity",
          "excess funds"
        ],
        "a": 1,
        "e": "It depends on foreign support.",
        "h": "Money.",
        "yr": "SS3"
      },
      {
        "q": "A problem of the OAU was the principle of:",
        "o": [
          "open borders",
          "common defence",
          "forced intervention",
          "non-interference in members' internal affairs"
        ],
        "a": 3,
        "e": "It limited action against abusive governments.",
        "h": "Sovereignty rule.",
        "yr": "SS3"
      },
      {
        "q": "How many states signed the OAU Charter in 1963?",
        "o": [
          "10",
          "9",
          "55",
          "32"
        ],
        "a": 3,
        "e": "Thirty-two independent states signed.",
        "h": "About thirty.",
        "yr": "SS3"
      },
      {
        "q": "Which country's leader championed the Casablanca group's radical view?",
        "o": [
          "Ghana (Kwame Nkrumah)",
          "Liberia",
          "Nigeria",
          "Ethiopia"
        ],
        "a": 0,
        "e": "Nkrumah wanted a political union of Africa.",
        "h": "Ghanaian.",
        "yr": "SS3"
      },
      {
        "q": "The AU's development programme is:",
        "o": [
          "NEPAD",
          "ECOMOG",
          "IMF",
          "OPEC"
        ],
        "a": 0,
        "e": "New Partnership for Africa's Development.",
        "h": "Development plan.",
        "yr": "SS3"
      },
      {
        "q": "Political instability and coups are:",
        "o": [
          "aims of the AU",
          "problems facing the AU",
          "achievements of the AU",
          "organs of the AU"
        ],
        "a": 1,
        "e": "They hinder African unity.",
        "h": "Obstacles.",
        "yr": "SS3"
      },
      {
        "q": "Ethnic and boundary disputes are:",
        "o": [
          "a charter",
          "a benefit",
          "a challenge to African unity",
          "a treaty"
        ],
        "a": 2,
        "e": "Colonial-era borders cause disputes.",
        "h": "Problem.",
        "yr": "SS3"
      },
      {
        "q": "The Pan-African Parliament is an organ of the:",
        "o": [
          "OPEC",
          "African Union",
          "OAU",
          "Commonwealth"
        ],
        "a": 1,
        "e": "It was created under the AU framework.",
        "h": "AU organ.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria's role in the OAU was as:",
        "o": [
          "a founding member with strong influence",
          "a colonial power",
          "an outsider",
          "a non-member"
        ],
        "a": 0,
        "e": "Nigeria was active in its formation.",
        "h": "Founding.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "International Organisation: Commonwealth",
    "topicCode": "SS3-GOV-09",
    "module": "International Organisations",
    "term": "First Term",
    "contentHTML": "<div class=\"learn-intro\">The <span class=\"learn-keyword\">Commonwealth of Nations</span> is a voluntary association of independent states, most of which were once part of the British Empire. Nigeria joined at independence in 1960.</div><h3 class=\"learn-subheading\">1. Origin</h3><p class=\"learn-p\">It evolved from the British Empire, was given a legal footing by the Statute of Westminster (1931), and was formalised by the London Declaration of 1949, which allowed republics such as India to remain members. The Commonwealth Secretariat was set up in London in 1965. Headship is symbolic and is held by the British monarch.</p><h3 class=\"learn-subheading\">2. Aims, Objectives and Principles</h3><ul style=\"margin-left:20px;\"><li>Promote democracy, human rights, the rule of law and good governance.</li><li>Promote economic and social development and cooperation.</li><li>Promote international peace and understanding.</li><li>Principles: equality of members, consensus in decision-making, respect for each member's sovereignty, and non-interference in internal affairs.</li></ul><h3 class=\"learn-subheading\">3. Organs</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Organ</th><th>Role</th></tr></thead><tbody><tr><td>Heads of Government Meeting (CHOGM)</td><td>Highest decision-making body; meets every two years</td></tr><tr><td>Commonwealth Secretariat</td><td>Administrative body headed by the Secretary-General</td></tr><tr><td>Commonwealth Foundation</td><td>Supports civil society and professional bodies</td></tr><tr><td>Commonwealth of Learning; Games Federation</td><td>Education and sports (Commonwealth Games)</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Achievements and Shortcomings</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Achievements</th><th>Shortcomings</th></tr></thead><tbody><tr><td>Helped end apartheid through sanctions and pressure</td><td>Lacks power to enforce decisions</td></tr><tr><td>Scholarships and technical assistance</td><td>Seen as a relic of colonialism, dominated by Britain</td></tr><tr><td>Election monitoring and democracy support</td><td>Diverse members with different interests; limited funding</td></tr><tr><td>Commonwealth Games and cultural links</td><td>Nigeria was suspended in 1995 after the execution of Ken Saro-Wiwa</td></tr></tbody></table></div>",
    "questions": [
      {
        "q": "The Commonwealth is an association of:",
        "o": [
          "independent states, mostly former British colonies",
          "military allies",
          "only European states",
          "only African states"
        ],
        "a": 0,
        "e": "It is a voluntary association.",
        "h": "British links.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria joined the Commonwealth in:",
        "o": [
          "1963",
          "1975",
          "1960",
          "1945"
        ],
        "a": 2,
        "e": "At independence.",
        "h": "Independence year.",
        "yr": "SS3"
      },
      {
        "q": "The Commonwealth grew out of the:",
        "o": [
          "British Empire",
          "French Empire",
          "Ottoman Empire",
          "Roman Empire"
        ],
        "a": 0,
        "e": "It evolved from the empire.",
        "h": "British.",
        "yr": "SS3"
      },
      {
        "q": "The Statute of Westminster was passed in:",
        "o": [
          "1960",
          "1965",
          "1949",
          "1931"
        ],
        "a": 3,
        "e": "It gave dominions legislative independence.",
        "h": "Early 1930s.",
        "yr": "SS3"
      },
      {
        "q": "The London Declaration of 1949 allowed:",
        "o": [
          "dissolution of the group",
          "republics to remain members",
          "expulsion of India",
          "end of the monarchy"
        ],
        "a": 1,
        "e": "It allowed India as a republic to stay.",
        "h": "Republics.",
        "yr": "SS3"
      },
      {
        "q": "The Commonwealth Secretariat was created in:",
        "o": [
          "1949",
          "1995",
          "1931",
          "1965"
        ],
        "a": 3,
        "e": "Set up in London.",
        "h": "Mid-1960s.",
        "yr": "SS3"
      },
      {
        "q": "The head of the Commonwealth is:",
        "o": [
          "the UN Secretary-General",
          "Nigeria's President",
          "the Prime Minister of India",
          "the British monarch (symbolic)"
        ],
        "a": 3,
        "e": "The headship is symbolic.",
        "h": "Royal.",
        "yr": "SS3"
      },
      {
        "q": "An aim of the Commonwealth is to:",
        "o": [
          "start wars",
          "impose British law",
          "promote democracy, human rights and development",
          "promote colonialism"
        ],
        "a": 2,
        "e": "It promotes shared values.",
        "h": "Democracy.",
        "yr": "SS3"
      },
      {
        "q": "Which is a principle of the Commonwealth?",
        "o": [
          "Compulsory decisions",
          "Equality of members",
          "Veto for some",
          "Rule by Britain"
        ],
        "a": 1,
        "e": "All members are equal.",
        "h": "Equal.",
        "yr": "SS3"
      },
      {
        "q": "The highest decision-making meeting of the Commonwealth is:",
        "o": [
          "G-8",
          "OPEC Conference",
          "ECOSOC",
          "CHOGM"
        ],
        "a": 3,
        "e": "Commonwealth Heads of Government Meeting.",
        "h": "Heads meet.",
        "yr": "SS3"
      },
      {
        "q": "CHOGM meets every:",
        "o": [
          "ten years",
          "year",
          "month",
          "two years"
        ],
        "a": 3,
        "e": "Held biennially.",
        "h": "It is a biennial meeting.",
        "yr": "SS3"
      },
      {
        "q": "The Commonwealth Games are:",
        "o": [
          "a trade fair",
          "a summit",
          "a court",
          "a sporting event held every four years"
        ],
        "a": 3,
        "e": "A multi-sport event among members.",
        "h": "Sports.",
        "yr": "SS3"
      },
      {
        "q": "A major achievement of the Commonwealth was:",
        "o": [
          "pressure against apartheid",
          "starting wars",
          "supporting apartheid",
          "ending trade"
        ],
        "a": 0,
        "e": "It helped isolate South Africa.",
        "h": "Anti-apartheid.",
        "yr": "SS3"
      },
      {
        "q": "Scholarships and technical assistance are:",
        "o": [
          "weapons",
          "problems of the Commonwealth",
          "achievements of the Commonwealth",
          "reasons for suspending members"
        ],
        "a": 2,
        "e": "It provides education and training help.",
        "h": "Aid.",
        "yr": "SS3"
      },
      {
        "q": "A shortcoming of the Commonwealth is that:",
        "o": [
          "it lacks power to enforce its decisions",
          "it is a military alliance",
          "it has too much power",
          "it controls members"
        ],
        "a": 0,
        "e": "It relies on persuasion.",
        "h": "No force.",
        "yr": "SS3"
      },
      {
        "q": "Some critics see the Commonwealth as:",
        "o": [
          "an economic union",
          "a relic of colonialism",
          "a new alliance",
          "a communist bloc"
        ],
        "a": 1,
        "e": "Because of British origins.",
        "h": "Old empire.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria was suspended from the Commonwealth in 1995 after:",
        "o": [
          "a coup",
          "the OPEC dispute",
          "the execution of Ken Saro-Wiwa",
          "the civil war"
        ],
        "a": 2,
        "e": "Rights violations under Abacha.",
        "h": "Ogoni activist.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria was readmitted to the Commonwealth in:",
        "o": [
          "1965",
          "1999",
          "1985",
          "1995"
        ],
        "a": 1,
        "e": "After return to civil rule.",
        "h": "Democracy restored.",
        "yr": "SS3"
      },
      {
        "q": "The Commonwealth Secretariat is located in:",
        "o": [
          "London",
          "Abuja",
          "New York",
          "Geneva"
        ],
        "a": 0,
        "e": "It is in London.",
        "h": "British capital.",
        "yr": "SS3"
      },
      {
        "q": "Election monitoring is a way the Commonwealth:",
        "o": [
          "supports democracy",
          "bans elections",
          "supports coups",
          "undermines democracy"
        ],
        "a": 0,
        "e": "It sends observers.",
        "h": "Democracy.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "International Organisation: ECOWAS",
    "topicCode": "SS3-GOV-10",
    "module": "International Organisations",
    "term": "Second Term",
    "contentHTML": "<div class=\"learn-intro\">The <span class=\"learn-keyword\">Economic Community of West African States (ECOWAS)</span> is a regional organisation founded on <span class=\"learn-keyword\">28 May 1975</span> by the Treaty of Lagos to promote economic integration among West African states. Its headquarters is in Abuja, Nigeria.</div><h3 class=\"learn-subheading\">1. Formation</h3><p class=\"learn-p\">Nigeria (under General Gowon) and Togo (under President Eyadema) led the initiative. Fifteen states signed the treaty at the start. The treaty was revised in 1993 to include political cooperation, peace and security. Members include Nigeria, Ghana, Senegal, Côte d'Ivoire, Benin, Togo and others; Mali, Burkina Faso and Niger announced their withdrawal, which took effect in 2025.</p><h3 class=\"learn-subheading\">2. Aims and Objectives</h3><ul style=\"margin-left:20px;\"><li>Promote economic integration and a single West African market.</li><li>Remove trade barriers and enable free movement of persons, goods and services.</li><li>Promote a common currency and monetary union.</li><li>Promote peace, security and stability in the sub-region.</li><li>Improve living standards and promote development.</li></ul><h3 class=\"learn-subheading\">3. Organs</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Organ</th><th>Function</th></tr></thead><tbody><tr><td>Authority of Heads of State and Government</td><td>Supreme decision-making body</td></tr><tr><td>Council of Ministers</td><td>Advises the Authority and oversees implementation</td></tr><tr><td>ECOWAS Commission</td><td>Executive and administrative arm (formerly the Executive Secretariat)</td></tr><tr><td>Community Parliament</td><td>Legislative and advisory body</td></tr><tr><td>Community Court of Justice</td><td>Interprets the treaty and settles disputes</td></tr><tr><td>Fund for Cooperation, Compensation and Development</td><td>Financial institution (Lomé)</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Achievements and Problems</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Achievements</th><th>Problems</th></tr></thead><tbody><tr><td>Free movement protocol and ECOWAS passport</td><td>Language barrier (English, French, Portuguese)</td></tr><tr><td>ECOMOG peacekeeping in Liberia and Sierra Leone</td><td>Poor transport and communication infrastructure</td></tr><tr><td>Promotion of trade and regional infrastructure</td><td>Political instability and coups; member withdrawals</td></tr><tr><td>Election monitoring and conflict mediation</td><td>Overlapping memberships; poor implementation of decisions; funding difficulties</td></tr></tbody></table></div>",
    "questions": [
      {
        "q": "ECOWAS was formed in:",
        "o": [
          "1963",
          "1990",
          "1975",
          "1960"
        ],
        "a": 2,
        "e": "The Treaty of Lagos was signed on 28 May 1975.",
        "h": "Mid-1970s.",
        "yr": "SS3"
      },
      {
        "q": "The treaty that established ECOWAS is the:",
        "o": [
          "Treaty of Versailles",
          "Treaty of Lagos",
          "Treaty of Abuja",
          "Treaty of Rome"
        ],
        "a": 1,
        "e": "Signed in Lagos in 1975.",
        "h": "A Nigerian city.",
        "yr": "SS3"
      },
      {
        "q": "Which Nigerian leader helped initiate ECOWAS?",
        "o": [
          "Olusegun Obasanjo",
          "General Yakubu Gowon",
          "Shehu Shagari",
          "General Buhari"
        ],
        "a": 1,
        "e": "Gowon and Togo's Eyadema led the initiative.",
        "h": "Head of State in 1975.",
        "yr": "SS3"
      },
      {
        "q": "The headquarters of ECOWAS is in:",
        "o": [
          "Abuja",
          "Dakar",
          "Lomé",
          "Accra"
        ],
        "a": 0,
        "e": "The Commission sits in Abuja.",
        "h": "Nigeria's capital.",
        "yr": "SS3"
      },
      {
        "q": "ECOWAS was revised in 1993 to include:",
        "o": [
          "military union with Europe",
          "only culture",
          "political cooperation, peace and security",
          "only trade"
        ],
        "a": 2,
        "e": "The revised treaty broadened its scope.",
        "h": "More than economics.",
        "yr": "SS3"
      },
      {
        "q": "An aim of ECOWAS is to:",
        "o": [
          "join NATO",
          "divide West Africa",
          "promote economic integration of West African states",
          "end regional trade"
        ],
        "a": 2,
        "e": "Its main goal.",
        "h": "Integration.",
        "yr": "SS3"
      },
      {
        "q": "ECOWAS promotes free movement of:",
        "o": [
          "persons, goods and services",
          "weapons only",
          "oil only",
          "soldiers only"
        ],
        "a": 0,
        "e": "Its protocol allows free movement of citizens.",
        "h": "People and goods.",
        "yr": "SS3"
      },
      {
        "q": "The supreme decision-making organ of ECOWAS is the:",
        "o": [
          "Authority of Heads of State and Government",
          "Community Parliament",
          "Community Court",
          "Council of Ministers"
        ],
        "a": 0,
        "e": "It takes major decisions.",
        "h": "Presidents.",
        "yr": "SS3"
      },
      {
        "q": "The ECOWAS organ that interprets the treaty is the:",
        "o": [
          "Community Court of Justice",
          "Community Parliament",
          "Commission",
          "Council of Ministers"
        ],
        "a": 0,
        "e": "It settles disputes over the treaty.",
        "h": "The court.",
        "yr": "SS3"
      },
      {
        "q": "The executive and administrative arm of ECOWAS is the:",
        "o": [
          "Community Parliament",
          "ECOWAS Commission",
          "Court of Justice",
          "Authority"
        ],
        "a": 1,
        "e": "It replaced the Executive Secretariat.",
        "h": "Commission.",
        "yr": "SS3"
      },
      {
        "q": "ECOMOG stands for:",
        "o": [
          "ECOWAS Monitoring Group",
          "Economic Community Military Organisation Group",
          "ECOWAS Ministry of Growth",
          "Economic Oil Management Group"
        ],
        "a": 0,
        "e": "It was an ECOWAS peacekeeping force.",
        "h": "Peacekeeping.",
        "yr": "SS3"
      },
      {
        "q": "ECOMOG intervened in:",
        "o": [
          "Kenya and Uganda",
          "Liberia and Sierra Leone",
          "Chad and Sudan only",
          "Egypt and Libya"
        ],
        "a": 1,
        "e": "Nigeria-led troops served there.",
        "h": "West African civil wars.",
        "yr": "SS3"
      },
      {
        "q": "Which is an achievement of ECOWAS?",
        "o": [
          "a single army",
          "removal of all borders",
          "a common flag for all",
          "Free movement protocol and ECOWAS passport"
        ],
        "a": 3,
        "e": "It eased movement across member states.",
        "h": "Travel.",
        "yr": "SS3"
      },
      {
        "q": "The ECOWAS Fund for Cooperation, Compensation and Development is based in:",
        "o": [
          "Accra",
          "Abuja",
          "Banjul",
          "Lomé"
        ],
        "a": 3,
        "e": "The fund is in Lomé, Togo.",
        "h": "Togo.",
        "yr": "SS3"
      },
      {
        "q": "A problem confronting ECOWAS is:",
        "o": [
          "the language barrier among English, French and Portuguese speakers",
          "too many funds",
          "perfect unity",
          "single language"
        ],
        "a": 0,
        "e": "Different colonial languages hinder integration.",
        "h": "Communication.",
        "yr": "SS3"
      },
      {
        "q": "Another problem of ECOWAS is:",
        "o": [
          "excess political stability",
          "political instability and coups",
          "no borders",
          "lack of members"
        ],
        "a": 1,
        "e": "Coups and conflicts hinder progress.",
        "h": "Instability.",
        "yr": "SS3"
      },
      {
        "q": "Poor transport and communication infrastructure:",
        "o": [
          "has no effect",
          "hinders regional trade",
          "improves integration",
          "boosts trade"
        ],
        "a": 1,
        "e": "Bad roads and links raise costs.",
        "h": "Infrastructure.",
        "yr": "SS3"
      },
      {
        "q": "Overlapping memberships in other regional groups can:",
        "o": [
          "end trade",
          "strengthen ECOWAS",
          "weaken commitment to ECOWAS",
          "cause peace"
        ],
        "a": 2,
        "e": "Members belong to several blocs.",
        "h": "Divided loyalty.",
        "yr": "SS3"
      },
      {
        "q": "Mali, Burkina Faso and Niger announced their withdrawal from ECOWAS following:",
        "o": [
          "coups and disputes with the bloc",
          "oil discoveries",
          "the Treaty of Lagos",
          "a new currency"
        ],
        "a": 0,
        "e": "They formed the Alliance of Sahel States after coups and sanctions.",
        "h": "Sahel states.",
        "yr": "SS3"
      },
      {
        "q": "The ECOWAS Community Parliament is:",
        "o": [
          "a fund",
          "a court",
          "a military unit",
          "a legislative and advisory body"
        ],
        "a": 3,
        "e": "It represents the peoples of member states.",
        "h": "Parliament.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "International Organisation: OPEC",
    "topicCode": "SS3-GOV-11",
    "module": "International Organisations",
    "term": "Second Term",
    "contentHTML": "<div class=\"learn-intro\">The <span class=\"learn-keyword\">Organisation of the Petroleum Exporting Countries (OPEC)</span> is an inter-governmental organisation of oil-producing states formed in <span class=\"learn-keyword\">1960</span> in Baghdad, Iraq, to coordinate petroleum policies. Its headquarters has been in Vienna, Austria, since 1965.</div><h3 class=\"learn-subheading\">1. Formation</h3><p class=\"learn-p\">The five founding members were Iran, Iraq, Kuwait, Saudi Arabia and Venezuela. They wanted to counter the power of the major Western oil companies (the 'Seven Sisters') and to secure fair prices. Nigeria joined in 1971, and other producers joined later.</p><h3 class=\"learn-subheading\">2. Aims and Objectives</h3><ul style=\"margin-left:20px;\"><li>Coordinate and unify petroleum policies of member states.</li><li>Stabilise oil prices and guarantee stable income to producers.</li><li>Ensure regular supply of petroleum to consuming nations.</li><li>Secure a fair return on capital for investors in the industry.</li><li>Safeguard the interests of member countries.</li></ul><h3 class=\"learn-subheading\">3. Organs</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Organ</th><th>Function</th></tr></thead><tbody><tr><td>Conference</td><td>Supreme authority; ministers of oil and energy meet twice a year</td></tr><tr><td>Board of Governors</td><td>Directs management and implements Conference decisions</td></tr><tr><td>Secretariat</td><td>Administrative and research work, headed by the Secretary-General</td></tr></tbody></table></div><h3 class=\"learn-subheading\">4. Achievements and Problems</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Achievements</th><th>Problems</th></tr></thead><tbody><tr><td>Gained control over member countries' oil resources</td><td>Disagreement among members over quotas and prices</td></tr><tr><td>Raised oil prices and revenue, notably in the 1970s</td><td>Cheating on production quotas</td></tr><tr><td>Provided aid and development funds to developing nations</td><td>Competition from non-OPEC producers and alternative energy</td></tr><tr><td>Coordinated production to stabilise the market</td><td>Political rivalries and wars among members; price instability</td></tr></tbody></table></div>",
    "questions": [
      {
        "q": "OPEC was formed in:",
        "o": [
          "1960",
          "1990",
          "1975",
          "1945"
        ],
        "a": 0,
        "e": "It was formed in Baghdad in September 1960.",
        "h": "Start of the decade.",
        "yr": "SS3"
      },
      {
        "q": "OPEC was formed in:",
        "o": [
          "Baghdad, Iraq",
          "Lagos",
          "Riyadh",
          "Vienna"
        ],
        "a": 0,
        "e": "The founding conference was in Baghdad.",
        "h": "Iraq.",
        "yr": "SS3"
      },
      {
        "q": "The headquarters of OPEC is in:",
        "o": [
          "Geneva",
          "Baghdad",
          "Vienna, Austria",
          "Abuja"
        ],
        "a": 2,
        "e": "Since 1965.",
        "h": "Austria.",
        "yr": "SS3"
      },
      {
        "q": "Which of these was a founding member of OPEC?",
        "o": [
          "Libya",
          "Venezuela",
          "Nigeria",
          "Angola"
        ],
        "a": 1,
        "e": "Founders: Iran, Iraq, Kuwait, Saudi Arabia, Venezuela.",
        "h": "South American.",
        "yr": "SS3"
      },
      {
        "q": "How many founding members did OPEC have?",
        "o": [
          "Ten",
          "Three",
          "Fifteen",
          "Five"
        ],
        "a": 3,
        "e": "Five states signed in Baghdad.",
        "h": "Five.",
        "yr": "SS3"
      },
      {
        "q": "Nigeria joined OPEC in:",
        "o": [
          "1990",
          "1960",
          "1971",
          "1975"
        ],
        "a": 2,
        "e": "Nigeria became a member in 1971.",
        "h": "Early 1970s.",
        "yr": "SS3"
      },
      {
        "q": "OPEC was formed mainly to:",
        "o": [
          "control the gold market",
          "fight wars",
          "coordinate petroleum policies and secure fair prices",
          "promote free trade in all goods"
        ],
        "a": 2,
        "e": "Producers wanted fair returns from oil.",
        "h": "Oil.",
        "yr": "SS3"
      },
      {
        "q": "An objective of OPEC is to:",
        "o": [
          "stabilise oil prices",
          "ban oil trade",
          "end oil production",
          "destabilise the market"
        ],
        "a": 0,
        "e": "It seeks stable prices and income.",
        "h": "Stability.",
        "yr": "SS3"
      },
      {
        "q": "OPEC also aims to ensure:",
        "o": [
          "shortages for consumers",
          "regular supply of petroleum to consuming nations",
          "oil boycott of all",
          "secret supply"
        ],
        "a": 1,
        "e": "Efficient, economic and regular supply.",
        "h": "Supply.",
        "yr": "SS3"
      },
      {
        "q": "The supreme authority of OPEC is the:",
        "o": [
          "Community Court",
          "Secretariat",
          "Board of Governors",
          "Conference"
        ],
        "a": 3,
        "e": "It is made up of oil ministers.",
        "h": "Ministers' meeting.",
        "yr": "SS3"
      },
      {
        "q": "The OPEC Conference usually meets:",
        "o": [
          "every ten years",
          "never",
          "every day",
          "twice a year"
        ],
        "a": 3,
        "e": "Regular ordinary sessions.",
        "h": "Biannual.",
        "yr": "SS3"
      },
      {
        "q": "The administrative organ of OPEC is the:",
        "o": [
          "Secretariat",
          "Parliament",
          "Court",
          "Conference"
        ],
        "a": 0,
        "e": "Headed by the Secretary-General.",
        "h": "Administration.",
        "yr": "SS3"
      },
      {
        "q": "The Board of Governors of OPEC:",
        "o": [
          "elects the UN head",
          "directs management and implements Conference decisions",
          "is a court",
          "makes all treaties"
        ],
        "a": 1,
        "e": "It supervises the running of OPEC.",
        "h": "Management.",
        "yr": "SS3"
      },
      {
        "q": "An achievement of OPEC is:",
        "o": [
          "losing control",
          "ending wars",
          "stopping oil production",
          "gaining control of member countries' oil resources"
        ],
        "a": 3,
        "e": "Ownership shifted from foreign companies.",
        "h": "Resource control.",
        "yr": "SS3"
      },
      {
        "q": "The 'Seven Sisters' were:",
        "o": [
          "UN organs",
          "African states",
          "the major Western oil companies",
          "OPEC members"
        ],
        "a": 2,
        "e": "OPEC countered their dominance.",
        "h": "Oil companies.",
        "yr": "SS3"
      },
      {
        "q": "A problem of OPEC is:",
        "o": [
          "lack of oil",
          "excess unity",
          "perfect agreement",
          "disagreement among members over quotas and prices"
        ],
        "a": 3,
        "e": "Members have different interests.",
        "h": "Disputes.",
        "yr": "SS3"
      },
      {
        "q": "Cheating on production quotas is:",
        "o": [
          "a challenge to OPEC discipline",
          "an organ",
          "an achievement",
          "an aim"
        ],
        "a": 0,
        "e": "Members sometimes overproduce.",
        "h": "Breaking rules.",
        "yr": "SS3"
      },
      {
        "q": "Competition from non-OPEC producers:",
        "o": [
          "weakens OPEC's influence on prices",
          "strengthens OPEC",
          "has no effect",
          "ends OPEC"
        ],
        "a": 0,
        "e": "Other producers increase supply.",
        "h": "Market share.",
        "yr": "SS3"
      },
      {
        "q": "Rising use of alternative energy is a:",
        "o": [
          "long-term challenge to OPEC",
          "boost to OPEC",
          "OPEC organ",
          "member of OPEC"
        ],
        "a": 0,
        "e": "It may reduce demand for oil.",
        "h": "Future risk.",
        "yr": "SS3"
      },
      {
        "q": "OPEC members are mainly:",
        "o": [
          "food-importing countries",
          "oil-exporting countries",
          "landlocked states",
          "industrial nations only"
        ],
        "a": 1,
        "e": "Producers of petroleum.",
        "h": "Exporters.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Millennium Development Goals (MDGs) and NEPAD",
    "topicCode": "SS3-GOV-12",
    "module": "International Development",
    "term": "Second Term",
    "contentHTML": "<div class=\"learn-intro\">The <span class=\"learn-keyword\">Millennium Development Goals (MDGs)</span> were eight time-bound development targets adopted by world leaders at the UN Millennium Summit in 2000, to be achieved by <span class=\"learn-keyword\">2015</span>. <span class=\"learn-keyword\">NEPAD</span> is an African programme for the continent's development.</div><h3 class=\"learn-subheading\">1. The Eight MDGs</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Goal</th><th>Target area</th></tr></thead><tbody><tr><td>1</td><td>Eradicate extreme poverty and hunger</td></tr><tr><td>2</td><td>Achieve universal primary education</td></tr><tr><td>3</td><td>Promote gender equality and empower women</td></tr><tr><td>4</td><td>Reduce child mortality</td></tr><tr><td>5</td><td>Improve maternal health</td></tr><tr><td>6</td><td>Combat HIV/AIDS, malaria and other diseases</td></tr><tr><td>7</td><td>Ensure environmental sustainability</td></tr><tr><td>8</td><td>Develop a global partnership for development</td></tr></tbody></table></div><h3 class=\"learn-subheading\">2. Achievements by 2015</h3><ul style=\"margin-left:20px;\"><li>Extreme poverty fell sharply worldwide.</li><li>Primary school enrolment rose, especially for girls.</li><li>Child and maternal deaths declined.</li><li>HIV/AIDS, malaria and TB interventions expanded.</li><li>Access to safe drinking water improved.</li><li>The MDGs were succeeded by the 17 Sustainable Development Goals (SDGs) for 2030.</li></ul><h3 class=\"learn-subheading\">3. NEPAD — New Partnership for Africa's Development</h3><p class=\"learn-p\">NEPAD was adopted in 2001 by the OAU (then becoming the AU), championed by Presidents Obasanjo (Nigeria), Mbeki (South Africa), Bouteflika (Algeria) and Wade (Senegal). It is a strategic framework for Africa's own development, and it became the AU's development agency (AUDA-NEPAD).</p><h3 class=\"learn-subheading\">4. Aims of NEPAD</h3><ul style=\"margin-left:20px;\"><li>Eradicate poverty and promote sustainable growth and development.</li><li>Promote good governance, democracy and peace.</li><li>Integrate Africa into the world economy.</li><li>Promote infrastructure, agriculture, health and education.</li><li>Attract investment; the African Peer Review Mechanism (APRM) monitors members' governance.</li></ul>",
    "questions": [
      {
        "q": "The MDGs were adopted in:",
        "o": [
          "2000",
          "1990",
          "2015",
          "1945"
        ],
        "a": 0,
        "e": "At the UN Millennium Summit.",
        "h": "New millennium.",
        "yr": "SS3"
      },
      {
        "q": "The MDGs were to be achieved by:",
        "o": [
          "2015",
          "2010",
          "2030",
          "2000"
        ],
        "a": 0,
        "e": "Target year 2015.",
        "h": "Fifteen-year plan.",
        "yr": "SS3"
      },
      {
        "q": "How many MDGs were there?",
        "o": [
          "Five",
          "Seventeen",
          "Twelve",
          "Eight"
        ],
        "a": 3,
        "e": "There were eight goals.",
        "h": "Eight.",
        "yr": "SS3"
      },
      {
        "q": "Which is the first MDG?",
        "o": [
          "Reduce child mortality",
          "Eradicate extreme poverty and hunger",
          "Universal primary education",
          "Combat HIV/AIDS"
        ],
        "a": 1,
        "e": "Goal 1.",
        "h": "Poverty.",
        "yr": "SS3"
      },
      {
        "q": "MDG 2 aimed at:",
        "o": [
          "maternal health",
          "gender equality",
          "environmental sustainability",
          "universal primary education"
        ],
        "a": 3,
        "e": "All children should complete primary school.",
        "h": "Education.",
        "yr": "SS3"
      },
      {
        "q": "MDG 3 aimed at:",
        "o": [
          "promoting gender equality and empowering women",
          "reducing poverty",
          "global partnership",
          "reducing child mortality"
        ],
        "a": 0,
        "e": "Goal 3.",
        "h": "Women.",
        "yr": "SS3"
      },
      {
        "q": "MDG 6 is concerned with:",
        "o": [
          "combating HIV/AIDS, malaria and other diseases",
          "poverty only",
          "primary education",
          "gender equality"
        ],
        "a": 0,
        "e": "Goal 6.",
        "h": "Diseases.",
        "yr": "SS3"
      },
      {
        "q": "MDG 7 is about:",
        "o": [
          "maternal health",
          "ensuring environmental sustainability",
          "child mortality",
          "education"
        ],
        "a": 1,
        "e": "Goal 7.",
        "h": "Environment.",
        "yr": "SS3"
      },
      {
        "q": "MDG 8 is about:",
        "o": [
          "universal education",
          "gender equality",
          "poverty",
          "developing a global partnership for development"
        ],
        "a": 3,
        "e": "Goal 8.",
        "h": "Partnership.",
        "yr": "SS3"
      },
      {
        "q": "MDG 4 and 5 aimed at:",
        "o": [
          "removing debt",
          "improving trade",
          "reducing child mortality and improving maternal health",
          "ending war"
        ],
        "a": 2,
        "e": "Health goals.",
        "h": "Mothers and children.",
        "yr": "SS3"
      },
      {
        "q": "One achievement of the MDGs by 2015 was:",
        "o": [
          "end of all diseases",
          "a sharp fall in extreme poverty worldwide",
          "universal peace",
          "complete end of poverty"
        ],
        "a": 1,
        "e": "Extreme poverty was greatly reduced.",
        "h": "Reduced, not eliminated.",
        "yr": "SS3"
      },
      {
        "q": "The MDGs were succeeded by:",
        "o": [
          "the Sustainable Development Goals (SDGs)",
          "ECOWAS",
          "NEPAD",
          "OPEC"
        ],
        "a": 0,
        "e": "SDGs 2016–2030.",
        "h": "Successor goals.",
        "yr": "SS3"
      },
      {
        "q": "How many SDGs are there?",
        "o": [
          "Twelve",
          "Twenty",
          "Seventeen",
          "Eight"
        ],
        "a": 2,
        "e": "There are 17 goals.",
        "h": "Seventeen.",
        "yr": "SS3"
      },
      {
        "q": "NEPAD stands for:",
        "o": [
          "National Economic Plan for African Development",
          "Nigerian Economic Partnership",
          "New Programme for African Democracy",
          "New Partnership for Africa's Development"
        ],
        "a": 3,
        "e": "It is an AU programme.",
        "h": "Partnership.",
        "yr": "SS3"
      },
      {
        "q": "NEPAD was adopted in:",
        "o": [
          "1963",
          "2015",
          "1975",
          "2001"
        ],
        "a": 3,
        "e": "Adopted by the OAU in 2001.",
        "h": "Early 2000s.",
        "yr": "SS3"
      },
      {
        "q": "NEPAD is a programme of the:",
        "o": [
          "African Union",
          "Commonwealth",
          "OPEC",
          "UN Security Council"
        ],
        "a": 0,
        "e": "It became the AU's development agency (AUDA-NEPAD).",
        "h": "African organisation.",
        "yr": "SS3"
      },
      {
        "q": "One of the leaders who championed NEPAD was Nigeria's:",
        "o": [
          "Yakubu Gowon",
          "Shehu Shagari",
          "Olusegun Obasanjo",
          "Sani Abacha"
        ],
        "a": 2,
        "e": "Along with Mbeki, Bouteflika and Wade.",
        "h": "President in 2001.",
        "yr": "SS3"
      },
      {
        "q": "An aim of NEPAD is to:",
        "o": [
          "stop trade",
          "eradicate poverty and promote sustainable growth",
          "promote colonialism",
          "increase poverty"
        ],
        "a": 1,
        "e": "Development of Africa.",
        "h": "Growth.",
        "yr": "SS3"
      },
      {
        "q": "The African Peer Review Mechanism (APRM) is used to:",
        "o": [
          "collect taxes",
          "fight wars",
          "monitor members' governance and policies",
          "set oil prices"
        ],
        "a": 2,
        "e": "Voluntary self-assessment under NEPAD.",
        "h": "Peer review.",
        "yr": "SS3"
      },
      {
        "q": "NEPAD seeks to integrate Africa into:",
        "o": [
          "a single colonial empire",
          "OPEC",
          "the world economy",
          "the Warsaw Pact"
        ],
        "a": 2,
        "e": "One of its major aims.",
        "h": "Global economy.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "E-Government, Leadership and Followership",
    "topicCode": "SS3-GOV-13",
    "module": "Governance and Leadership",
    "term": "Second Term",
    "contentHTML": "<div class=\"learn-intro\"><span class=\"learn-keyword\">E-government</span> is the use of information and communication technology (ICT) by government to deliver services and interact with citizens. <span class=\"learn-keyword\">Leadership</span> and <span class=\"learn-keyword\">followership</span> are the two sides of any political relationship.</div><h3 class=\"learn-subheading\">1. Meaning and Examples of E-Government</h3><p class=\"learn-p\">E-government means using the internet, computers and mobile technology to provide government information and services. Examples: online tax payment, e-passport and visa applications, National Identification Number (NIN) registration, online result checking and school admission portals, e-voting, and digital payment platforms such as the Treasury Single Account (TSA).</p><h3 class=\"learn-subheading\">2. Advantages and Disadvantages</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Advantages</th><th>Disadvantages</th></tr></thead><tbody><tr><td>Faster, cheaper and more efficient services</td><td>High cost of setting up and maintaining systems</td></tr><tr><td>Greater transparency and less corruption</td><td>Poor electricity and internet access</td></tr><tr><td>Convenience and access for citizens</td><td>Digital illiteracy; excludes the poor and rural people</td></tr><tr><td>Better record keeping and planning</td><td>Cyber crime, hacking and privacy risks</td></tr></tbody></table></div><h3 class=\"learn-subheading\">3. Leadership and Followership</h3><p class=\"learn-p\">A leader is a person who guides, directs and influences others toward a goal; followers are those who accept and support the leader. Leadership is only effective when followers cooperate.</p><h3 class=\"learn-subheading\">4. Qualities of a Good Leader</h3><ul style=\"margin-left:20px;\"><li>Honesty, integrity and accountability.</li><li>Vision and the ability to plan.</li><li>Courage and decisiveness.</li><li>Fairness and impartiality.</li><li>Good communication and listening skills.</li><li>Humility and readiness to serve; ability to inspire and motivate.</li></ul><h3 class=\"learn-subheading\">5. Roles of Leaders and Followers</h3><div class=\"learn-table-wrap\"><table class=\"learn-table\"><thead><tr><th>Leaders</th><th>Followers</th></tr></thead><tbody><tr><td>Make policies and laws; provide direction</td><td>Obey laws and support legitimate government</td></tr><tr><td>Maintain law, order and security</td><td>Pay taxes and take part in elections</td></tr><tr><td>Provide services and manage resources</td><td>Offer constructive criticism and hold leaders accountable</td></tr><tr><td>Represent the nation at home and abroad</td><td>Participate in community and national development</td></tr></tbody></table></div>",
    "questions": [
      {
        "q": "E-government means:",
        "o": [
          "using ICT to deliver government services",
          "an oil policy",
          "a military government",
          "an electoral ban"
        ],
        "a": 0,
        "e": "Use of information technology in government.",
        "h": "Electronic.",
        "yr": "SS3"
      },
      {
        "q": "An example of e-government is:",
        "o": [
          "handwritten registers",
          "online payment of taxes",
          "door-to-door tax collection",
          "town crier announcement"
        ],
        "a": 1,
        "e": "Digital services.",
        "h": "Online.",
        "yr": "SS3"
      },
      {
        "q": "The National Identification Number (NIN) registration is an example of:",
        "o": [
          "e-government",
          "colonial policy",
          "military rule",
          "barter"
        ],
        "a": 0,
        "e": "Digital ID service.",
        "h": "Digital service.",
        "yr": "SS3"
      },
      {
        "q": "An advantage of e-government is:",
        "o": [
          "more paperwork",
          "faster and more efficient service delivery",
          "increased delays",
          "higher corruption"
        ],
        "a": 1,
        "e": "Saves time and cost.",
        "h": "Efficiency.",
        "yr": "SS3"
      },
      {
        "q": "E-government reduces corruption by:",
        "o": [
          "increasing transparency",
          "increasing cash handling",
          "hiding records",
          "reducing accountability"
        ],
        "a": 0,
        "e": "Digital records are traceable.",
        "h": "Transparency.",
        "yr": "SS3"
      },
      {
        "q": "A disadvantage of e-government is:",
        "o": [
          "poor electricity and internet access",
          "more convenience",
          "lower cost for all",
          "faster service"
        ],
        "a": 0,
        "e": "Infrastructure limits access.",
        "h": "Infrastructure.",
        "yr": "SS3"
      },
      {
        "q": "Digital illiteracy means:",
        "o": [
          "inability to use digital technology",
          "ability to code",
          "having a computer",
          "owning a phone"
        ],
        "a": 0,
        "e": "It excludes many citizens.",
        "h": "Skills gap.",
        "yr": "SS3"
      },
      {
        "q": "Hacking and cyber crime are:",
        "o": [
          "types of voting",
          "advantages",
          "risks of e-government",
          "forms of leadership"
        ],
        "a": 2,
        "e": "Systems can be attacked.",
        "h": "Security.",
        "yr": "SS3"
      },
      {
        "q": "The Treasury Single Account (TSA) is an example of:",
        "o": [
          "digital financial management",
          "military rule",
          "election rigging",
          "colonial tax"
        ],
        "a": 0,
        "e": "It centralises government revenue.",
        "h": "Money.",
        "yr": "SS3"
      },
      {
        "q": "A leader is a person who:",
        "o": [
          "avoids decisions",
          "only obeys orders",
          "rules by force only",
          "guides and influences others toward a goal"
        ],
        "a": 3,
        "e": "Leadership involves direction.",
        "h": "Guide.",
        "yr": "SS3"
      },
      {
        "q": "A follower is one who:",
        "o": [
          "accepts and supports a leader",
          "ignores everyone",
          "rules the group",
          "makes laws alone"
        ],
        "a": 0,
        "e": "Followers cooperate with leaders.",
        "h": "Support.",
        "yr": "SS3"
      },
      {
        "q": "Which is a quality of a good leader?",
        "o": [
          "Cowardice",
          "Selfishness",
          "Honesty and integrity",
          "Dishonesty"
        ],
        "a": 2,
        "e": "Good leaders are trustworthy.",
        "h": "Character.",
        "yr": "SS3"
      },
      {
        "q": "A good leader must have:",
        "o": [
          "fear of decision",
          "no plan",
          "dislike for advice",
          "vision and the ability to plan"
        ],
        "a": 3,
        "e": "Direction for the people.",
        "h": "Foresight.",
        "yr": "SS3"
      },
      {
        "q": "Fairness and impartiality means a leader:",
        "o": [
          "favours relatives",
          "punishes critics",
          "ignores laws",
          "treats all people equally"
        ],
        "a": 3,
        "e": "Justice for all.",
        "h": "Equal treatment.",
        "yr": "SS3"
      },
      {
        "q": "A role of leaders in government is to:",
        "o": [
          "avoid responsibility",
          "disobey laws",
          "make policies and provide security",
          "block development"
        ],
        "a": 2,
        "e": "They govern and protect.",
        "h": "Governing.",
        "yr": "SS3"
      },
      {
        "q": "A role of followers is to:",
        "o": [
          "disobey laws",
          "avoid voting",
          "obey laws and pay taxes",
          "destroy property"
        ],
        "a": 2,
        "e": "Citizens' duties.",
        "h": "Duty.",
        "yr": "SS3"
      },
      {
        "q": "Followers hold leaders accountable through:",
        "o": [
          "rumour",
          "boycott of law",
          "constructive criticism and voting",
          "violence"
        ],
        "a": 2,
        "e": "Peaceful means.",
        "h": "Accountability.",
        "yr": "SS3"
      },
      {
        "q": "Leadership is effective only when:",
        "o": [
          "laws are ignored",
          "followers are absent",
          "followers cooperate",
          "leaders are forced"
        ],
        "a": 2,
        "e": "Both sides matter.",
        "h": "Two-way.",
        "yr": "SS3"
      },
      {
        "q": "Good communication skills help a leader to:",
        "o": [
          "hide information",
          "inspire and mobilise people",
          "confuse people",
          "cause conflict"
        ],
        "a": 1,
        "e": "Clear communication.",
        "h": "Speak well.",
        "yr": "SS3"
      },
      {
        "q": "Humility in a leader means:",
        "o": [
          "readiness to serve and listen",
          "dictatorship",
          "arrogance",
          "selfishness"
        ],
        "a": 0,
        "e": "Servant leadership.",
        "h": "Modesty.",
        "yr": "SS3"
      }
    ]
  }
];
